/**
 * API Service Layer for BePono Frontend
 * 
 * This file contains all the HTTP API calls to the BePono backend.
 * It's organized into logical groups (auth, family, behavior, goals)
 * and handles:
 * - JWT token management
 * - Request/response data transformation
 * - Error handling
 * - Type safety with TypeScript
 * 
 * The API uses axios for HTTP requests with automatic token injection.
 */

import axios from 'axios';
import { AuthResponse, LoginRequest, RegisterRequest, Child, Family, FamilySummary, RecordBehaviorRequest, RecordBehaviorResponse, BehaviorRecord, BehaviorType, PagedBehaviorResponse, DailyProgress, Goal, CreateGoalRequest, EnhancedFamilyDashboard, ChildAvatarUploadResponse, ChildAvatarDeleteResponse, AvatarUploadRequest, RoleSuggestion, CreateRoleSuggestionRequest, UpdateRoleSuggestionRequest, PolicyDocument, PolicyDocumentHistory, PolicyDocumentType, UpdatePolicyRequest, PolicyAcknowledgmentRequest, UserPolicyAcknowledgment, PaginatedPolicyAcknowledgments, AlexaIntegrationStatusDto, RefreshTokenResponse, UserAuthProvider, CanRemoveAuthProviderResponse, DevelopmentalAverageDto, SubscriptionStatusResponse, CreateCheckoutRequest, CreateCheckoutResponse, ChoreList, ChoreListPeriodStatus, CreateChoreListRequest, UpdateChoreListRequest, ChoreHistoryResponse, FamilyChoresSummary, MarkItemCompleteResponse, MakeItRightTemplate, UpdateMakeItRightTemplateRequest, SiteDefaultMakeItRightTasks, ResetAndReflectTemplate, UpdateResetAndReflectTemplateRequest, SiteDefaultResetAndReflectTasks, OneOnOneTimeTemplate, UpdateOneOnOneTimeTemplateRequest, SiteDefaultOneOnOneTimeTasks, PagedGoalResponse, NukeResult, CalendarConnectionDto, CalendarSourceDto, CreateCalendarConnectionDto, ChildCalendarEventsDto, ChildCalendarSelectionDto, CalendarOAuthStartDto, CalendarProviderType, CalendarConnectionHealthDto, UpdateCalDavCredentialsDto, FamilyAnalyticsResponse, RawBehaviorDto, FamilyDetailDto, FamilyMemberDto, InvitationDto, ChildAccessTokenDto, PagedApiResponse, ChildDashboardDto, MeltdownTrackerDto, TriangleTrackerDto, OneOnOneTimeTrackerDto, GoalRecalculationResultDto, UserVoiceIntegrationDto, FamilyVoiceIntegrationStatusDto, FamilyAwaySchedule, CrossingChildPreferencesDto } from '../types/api';
import { KBArticleListDto, KBVideoDto } from '../types/knowledgeBase';
import { isAuthPage } from '../utils/authUtils';
import { getDateInFamilyTimezone, getDateRangeFromTimeWindow } from '../utils/dateUtils';
import { IMPERSONATION_CONSTANTS } from '../constants/impersonation';

// API base URL - configurable via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

// Module-level access token — avoids localStorage XSS exposure.
// The HttpOnly refresh token cookie is managed by the browser automatically.
let currentAccessToken: string | null = null;

export const getCurrentAccessToken = (): string | null => currentAccessToken;
export const setCurrentAccessToken = (token: string | null) => { currentAccessToken = token; };

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // required for the HttpOnly refresh token cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to automatically add JWT tokens and impersonation headers to API calls
 *
 * This ensures all authenticated endpoints receive the user's token
 * and impersonation context without having to manually add it to each request.
 */
// Session storage key for impersonated user ID (minimal storage for page refresh support)
const IMPERSONATED_USER_ID_KEY = 'impersonatedUserId';

// Module-level variable for impersonation state
let currentImpersonatedUserId: number | null = null;

// Initialize from sessionStorage on module load (survives page refresh)
try {
  const stored = sessionStorage.getItem(IMPERSONATED_USER_ID_KEY);
  if (stored) {
    currentImpersonatedUserId = parseInt(stored, 10);
  }
} catch (error) {
  console.error('Failed to load impersonation state:', error);
}

// Export function to read current impersonation state (used for logout audit and testing)
export const getCurrentImpersonatedUserId = (): number | null => currentImpersonatedUserId;

// Export function to update impersonation state (called by useImpersonation hook)
export const setImpersonatedUserId = (userId: number | null) => {
  currentImpersonatedUserId = userId;

  // Persist to sessionStorage for page refresh support
  if (userId !== null) {
    sessionStorage.setItem(IMPERSONATED_USER_ID_KEY, userId.toString());
  } else {
    sessionStorage.removeItem(IMPERSONATED_USER_ID_KEY);
  }
};

api.interceptors.request.use((config) => {
  const token = currentAccessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add impersonation header if currently impersonating
  if (currentImpersonatedUserId && config.headers) {
    config.headers[IMPERSONATION_CONSTANTS.HEADER_NAME] = currentImpersonatedUserId.toString();
  }

  return config;
});

/**
 * Response interceptor to handle 401 unauthorized errors with automatic token refresh
 * 
 * When the server returns a 401 (unauthorized) response:
 * - First attempts to refresh the access token using the stored refresh token
 * - If refresh succeeds, retries the original request with the new access token
 * - If refresh fails, clears auth data and redirects to login
 * - Prevents multiple refresh attempts for concurrent 401 errors
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Helper function to add request to refresh queue
const addSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Helper function to notify all waiting requests
const notifySubscribers = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Check if this is a 401 error
    if (error.response?.status === 401) {
      // Skip refresh for auth endpoints
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                           originalRequest?.url?.includes('/auth/register') ||
                           originalRequest?.url?.includes('/auth/refresh');
      
      if (!isAuthEndpoint && !originalRequest._retry) {
        originalRequest._retry = true;
        
        // Check if we're already refreshing
        if (isRefreshing) {
          // Queue this request to be retried after refresh completes
          return new Promise((resolve) => {
            addSubscriber((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            });
          });
        }
        
        isRefreshing = true;
        
        try {
          // Attempt to refresh the token
          const refreshResult = await authAPI.refreshToken();
          
          if (refreshResult) {
            // Refresh succeeded, update authorization header and retry original request
            originalRequest.headers.Authorization = `Bearer ${refreshResult.accessToken}`;
            
            // Notify all queued requests
            notifySubscribers(refreshResult.accessToken);
            
            isRefreshing = false;
            
            // Retry the original request
            return api(originalRequest);
          } else {
            // Refresh failed, redirect to login
            isRefreshing = false;
            notifySubscribers('');
            redirectToLogin();
          }
        } catch {
          // Refresh failed, redirect to login
          isRefreshing = false;
          notifySubscribers('');
          redirectToLogin();
        }
      } else if (isAuthEndpoint) {
        // Auth endpoint returned 401, don't attempt refresh
        return Promise.reject(error);
      }
    }

    // Handle 403 "Account locked" - emergency lockout by admin
    if (error.response?.status === 403 && error.response?.data?.error === 'Account locked') {
      console.error('Account locked by admin — redirecting to login');
      // Clear all auth data and redirect to login with lockout error
      setImpersonatedUserId(null);
      currentAccessToken = null;
      localStorage.removeItem('user');
      window.location.href = '/login?error=account_locked';
      return new Promise(() => {}); // Never resolves — page is redirecting
    }

    // Handle 403 Forbidden errors during impersonation (read-only mode)
    if (error.response?.status === 403 && error.response?.data?.error?.includes('impersonating')) {
      console.error('Action blocked during impersonation:', error.response.data);
      // UI components should handle this gracefully by disabling buttons
      // This is a fallback in case a button wasn't disabled
    }

    return Promise.reject(error);
  }
);

// Helper function to handle redirect to login
const redirectToLogin = () => {
  // Clear auth data
  authAPI.logout();
  
  // Get current URL for redirect
  const currentPath = window.location.pathname + window.location.search;
  
  // Determine redirect URL
  let redirectUrl = '/login';
  
  // Only add redirect parameter for non-auth pages
  if (!isAuthPage(currentPath) && currentPath !== '/') {
    // Encode the redirect URL
    redirectUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }
  
  // Redirect to login
  window.location.href = redirectUrl;
};

/**
 * Authentication API
 * 
 * Handles user login, registration, and token management.
 * Uses localStorage to persist authentication state across browser sessions.
 */
export const authAPI = {
  /**
   * Authenticates a user with email and password
   * Returns user info and JWT token on success
   * Issue #122 - Includes browser timezone for daily reset calculations
   */
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    // Detect browser timezone for daily reset calculations (issue #122)
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const loginData = {
      ...data,
      browserTimezone
    };
    const response = await api.post<AuthResponse>('/api/auth/login', loginData);
    
    // TODO: Remove this workaround once backend sends nextAction
    // For now, check if user has a family to determine routing
    if (!response.data.nextAction) {
      try {
        // Temporarily set access token so the family check can use it
        const prevToken = currentAccessToken;
        currentAccessToken = response.data.token;

        const families = await api.get<FamilySummary[]>('/api/family/list');
        response.data.nextAction = families.data.length > 0 ? 'dashboard' : 'family_setup';
        response.data.hasFamily = families.data.length > 0;

        // Restore previous token state
        currentAccessToken = prevToken;
      } catch {
        // If family check fails, assume they need setup
        response.data.nextAction = 'family_setup';
        response.data.hasFamily = false;
      }
    }
    
    return response.data;
  },

  /**
   * Creates a new user account
   * Returns user info and JWT token on success
   */
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/api/auth/register', data);
    
    // TODO: Remove this workaround once backend sends nextAction
    // New registrations always go to family setup
    if (!response.data.nextAction) {
      response.data.nextAction = 'family_setup';
      response.data.hasFamily = false;
    }
    
    return response.data;
  },

  /**
   * Logs out the current user by clearing local storage and impersonation state
   * Note: This is client-side only - the server token remains valid until expiry
   */
  logout: () => {
    setImpersonatedUserId(null);
    currentAccessToken = null;
    localStorage.removeItem('user');
  },

  /**
   * Gets the currently logged-in user from localStorage
   * Returns null if no user is logged in
   */
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Stores authentication data in localStorage
   * Called after successful login/registration to persist the session
   */
  setAuth: (authResponse: AuthResponse) => {
    currentAccessToken = authResponse.token;

    localStorage.setItem('user', JSON.stringify({
      userId: authResponse.userId,
      email: authResponse.email,
      firstName: authResponse.firstName,
      lastName: authResponse.lastName,
      profileImageUrl: authResponse.profileImageUrl,
      isAdmin: authResponse.isAdmin,
      emailVerificationStatus: authResponse.emailVerificationStatus,
      createdAt: authResponse.createdAt,
    }));
  },

  /**
   * Refreshes the access token using the stored refresh token
   * Returns new tokens or null if refresh fails
   */
  refreshToken: async (): Promise<RefreshTokenResponse | null> => {
    try {
      // The HttpOnly refresh token cookie is sent automatically via withCredentials.
      // No request body needed.
      const response = await api.post<RefreshTokenResponse>('/api/auth/refresh');

      // Update the module-level access token with the new one
      currentAccessToken = response.data.accessToken;

      return response.data;
    } catch {
      // Refresh failed, clear all auth data
      authAPI.logout();
      return null;
    }
  },

  /**
   * Initiates password reset process by sending email with reset link
   * Takes email address and returns success message (always returns success for security)
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/api/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Resets user password using the token from reset email
   * Takes reset token and new password, returns success message
   */
  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/api/auth/reset-password', {
      token,
      newPassword
    });
    return response.data;
  },

  /**
   * Verifies user email using the token from verification email (Issue #986)
   * Takes verification token and returns success message
   */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/api/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Resends email verification link to user (Issue #986)
   * Takes email address and returns success message (with 5-minute rate limiting)
   */
  resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/api/auth/resend-verification-email', { email });
    return response.data;
  },
};

/**
 * Family and Children API
 * 
 * Handles family management and child profiles.
 * Families are the organizational unit that contains children.
 * Multiple adults can be members of a family.
 */
// Developmental Averages API - Public access for analytics
export const developmentalAveragesAPI = {
  getAll: async (): Promise<DevelopmentalAverageDto[]> => {
    const response = await api.get<DevelopmentalAverageDto[]>('/api/developmental-averages');
    return response.data;
  },
};

export const familyAPI = {
  /**
   * Gets all families the current user is a member of
   * Transforms API response to match frontend Family interface
   */
  getFamilies: async (): Promise<Family[]> => {
    const response = await api.get<FamilySummary[]>('/api/family/list');
    // Convert FamilySummary to Family format for frontend compatibility
    return response.data.map(family => ({
      familyId: family.id,
      name: family.name,
      description: '',
      ownerId: 0, // Will be set properly when we get family details
      inviteCode: '',
      createdAt: new Date().toISOString()
    }));
  },

  /**
   * Gets detailed family information including correct createdAt timestamp
   * Fixes Issue #210 - family creation date validation bug
   */
  getFamilyDetails: async (familyId: number): Promise<Family> => {
    const response = await api.get<FamilyDetailDto>(`/api/family/${familyId}`);
    // Convert backend FamilyDto to frontend Family interface
    return {
      familyId: response.data.id,
      name: response.data.name,
      description: response.data.description || '',
      ownerId: response.data.createdByUserId,
      inviteCode: '', // Not included in FamilyDto, would need separate call if needed
      timezone: response.data.timezone,
      createdAt: response.data.createdAt, // This is the correct creation timestamp!
      effectiveDailyLoggingGoalPerChild: response.data.effectiveDailyLoggingGoalPerChild,
      effectiveThemeName: response.data.effectiveThemeName,
      effectiveLabelLanguageMode: response.data.effectiveLabelLanguageMode
    };
  },

  /**
   * Creates a new family with the current user as owner
   */
  createFamily: async (name: string, description?: string): Promise<Family> => {
    const response = await api.post<Family>('/api/family', { name, description });
    return response.data;
  },

  /**
   * Gets all children in a family
   * Handles API inconsistency where some endpoints return 'id' vs 'childId'
   */
  getChildren: async (familyId: number): Promise<Child[]> => {
    const response = await api.get<Child[]>(`/api/family/${familyId}/children`);
    // Map API response to frontend format, ensuring consistent childId field
    return response.data.map(child => ({
      ...child,
      childId: child.id || child.childId || 0,
    }));
  },

  /**
   * Adds a new child to a family
   * Converts frontend date format to API's separate year/month/day fields
   */
  addChild: async (familyId: number, childData: {
    name: string;
    dateOfBirth: string;
    aliases?: string[];
    gender?: string;
    mantra?: string;
    familyLoveQuestion?: string;
    familyNeedQuestion?: string;
  }): Promise<Child> => {
    // Convert dateOfBirth (YYYY-MM-DD) to separate year, month, day fields
    const date = new Date(childData.dateOfBirth);
    const birthYear = date.getFullYear();
    const birthMonth = date.getMonth() + 1; // JavaScript months are 0-based
    const birthDay = date.getDate();

    const response = await api.post<Child>(`/api/family/${familyId}/children`, {
      name: childData.name,
      aliases: childData.aliases || [],
      birthYear,
      birthMonth,
      birthDay,
      gender: childData.gender || null,
      mantra: childData.mantra || null,
      familyLoveQuestion: childData.familyLoveQuestion || null,
      familyNeedQuestion: childData.familyNeedQuestion || null,
    });
    // Map API response to frontend format
    const child = response.data;
    return {
      ...child,
      childId: child.id || child.childId || 0,
    };
  },

  /**
   * Gets a specific child by ID
   */
  getChild: async (childId: number): Promise<Child> => {
    const response = await api.get<Child>(`/api/family/children/${childId}`);
    const child = response.data;
    return {
      ...child,
      childId: child.id || child.childId || 0,
    };
  },

  /**
   * Gets all goals for a family (used by enhanced dashboard)
   */
  getGoals: async (familyId: number): Promise<Goal[]> => {
    const response = await api.get<Goal[]>(`/api/goal/family/${familyId}`);
    return response.data;
  },

  /**
   * Gets enhanced family dashboard with context for behavior decisions
   * Issue #108 - provides today's behavior counts, ladder positions, and goal progress
   * Issue #151 - uses family timezone for daily reset calculations, with browser fallback
   */
  getEnhancedDashboard: async (params?: { familyTimezone?: string; browserTimezone?: string }): Promise<EnhancedFamilyDashboard> => {
    // Use provided timezone parameters or detect browser timezone as fallback
    const queryParams: Record<string, string> = {};
    
    if (params?.familyTimezone) {
      queryParams.familyTimezone = params.familyTimezone;
    } else if (params?.browserTimezone) {
      queryParams.browserTimezone = params.browserTimezone;
    } else {
      // Final fallback to browser-detected timezone
      queryParams.browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    
    const response = await api.get<EnhancedFamilyDashboard>('/api/family/dashboard/enhanced', {
      params: queryParams
    });
    return response.data;
  },

  /**
   * Gets the family's timezone for calculations
   * Issue #151 - retrieves family timezone for analytics and date calculations
   */
  getFamilyTimezone: async (familyId: number): Promise<string> => {
    const response = await api.get<string>(`/api/family/${familyId}/timezone`);
    return response.data;
  },

  /**
   * Updates family information (name, timezone, etc.)
   * Note: Backend implementation needed
   */
  updateFamilyInfo: async (familyId: number, familyInfo: { familyName: string; timezone: string }): Promise<void> => {
    await api.put<void>(`/api/family/${familyId}`, {
      name: familyInfo.familyName,
      timezone: familyInfo.timezone
    });
  },

  /**
   * Gets family members with roles and permissions
   */
  getFamilyMembers: async (familyId: number): Promise<FamilyMemberDto[]> => {
    const response = await api.get<FamilyMemberDto[]>(`/api/family/${familyId}/members`);
    return response.data;
  },

  /**
   * Adds a new family member via invitation email or direct addition
   * Email method: Sends invitation email and returns InvitationDto
   * Direct method: Creates user account immediately and returns FamilyMemberDto
   */
  addFamilyMember: async (familyId: number, memberData: {
    firstName: string;
    lastName: string;
    memberEmail: string;
    memberRole: string;
    isPrimaryCaregiver: boolean;
    invitationMethod: 'email' | 'direct';
    mantra?: string;
    familyLoveQuestion?: string;
    familyNeedQuestion?: string;
    customMessage?: string;
    password?: string;
  }): Promise<InvitationDto | FamilyMemberDto> => {
    if (memberData.invitationMethod === 'email') {
      // Create invitation (sends email)
      const inviteDto = {
        email: memberData.memberEmail,
        role: memberData.memberRole === 'Owner' ? 1 : 2, // Map to FamilyRole enum
        customRole: memberData.memberRole,
        message: memberData.customMessage || '',
        firstName: memberData.firstName,
        lastName: memberData.lastName
      };

      const response = await api.post(`/api/family/${familyId}/invite`, inviteDto);
      return response.data; // Return the InvitationDto from the backend
    } else {
      // Direct addition (creates user immediately)
      const directAddDto = {
        firstName: memberData.firstName,
        lastName: memberData.lastName,
        email: memberData.memberEmail,
        role: memberData.memberRole === 'Owner' ? 1 : 2, // Map to FamilyRole enum
        isPrimaryCaregiver: memberData.isPrimaryCaregiver,
        mantra: memberData.mantra || '',
        familyLoveQuestion: memberData.familyLoveQuestion || '',
        familyNeedQuestion: memberData.familyNeedQuestion || '',
        customMessage: memberData.customMessage || '',
        customRole: memberData.memberRole, // Send the actual role name
        password: memberData.password
      };

      const response = await api.post(`/api/family/${familyId}/members`, directAddDto);
      return response.data; // Return the FamilyMemberDto from the backend
    }
  },

  /**
   * Removes a family member from the family
   * Note: Backend implementation needed
   */
  removeFamilyMember: async (familyId: number, userId: number): Promise<void> => {
    await api.delete<void>(`/api/family/${familyId}/members/${userId}`);
  },

  /**
   * Updates child profile information
   */
  updateChild: async (childId: number, childData: {
    childName: string;
    aliases: string[];
    birthYear: number;
    birthMonth?: number;
    gender?: string;
    mantra?: string;
    familyLoveQuestion?: string;
    familyNeedQuestion?: string;
  }): Promise<void> => {
    await api.put<void>(`/api/family/children/${childId}`, {
      name: childData.childName,
      aliases: childData.aliases,
      birthYear: childData.birthYear,
      birthMonth: childData.birthMonth,
      gender: childData.gender,
      mantra: childData.mantra,
      familyLoveQuestion: childData.familyLoveQuestion,
      familyNeedQuestion: childData.familyNeedQuestion
    });
  },

  /**
   * Deletes a child from the family
   */
  deleteChild: async (childId: number): Promise<void> => {
    await api.delete<void>(`/api/family/children/${childId}`);
  },

  /**
   * Removes a child from the family (soft delete)
   * Issue #174 - implements child removal functionality
   */
  removeChild: async (childId: number): Promise<{ success: boolean }> => {
    await api.post(`/api/admin/children/${childId}/soft-delete`);
    return { success: true };
  },

  /**
   * Gets child access tokens for dashboard URLs
   * Note: Backend implementation needed
   */
  getChildAccessTokens: async (familyId: number): Promise<ChildAccessTokenDto[]> => {
    const response = await api.get<ChildAccessTokenDto[]>(`/api/family/${familyId}/child-access-tokens`);
    return response.data;
  },

  /**
   * Regenerates a child access token for a specific dashboard
   * Note: Backend implementation needed
   */
  regenerateChildAccessToken: async (childId: number, dashboardType: string): Promise<{ accessUrl: string }> => {
    const response = await api.post<{ accessUrl: string }>(`/api/family/children/${childId}/regenerate-token`, {
      dashboardType
    });
    return response.data;
  },

  /**
   * Gets child dashboard data using URL slug (Issue #617)
   * Used for passwordless child dashboard access
   */
  getDashboardBySlug: async (urlSlug: string): Promise<ChildDashboardDto> => {
    const response = await api.get<ChildDashboardDto>(`/api/child-dashboard/${urlSlug}`);
    return response.data;
  },

  /**
   * Uploads a new avatar for a child
   * Issue #137 - Child avatar management with primary caregiver permissions
   * @param childId - The ID of the child to update
   * @param imageData - Base64 encoded image data
   * @returns Promise containing the uploaded avatar URL
   * @throws Error if user lacks permission or request fails
   */
  uploadChildAvatar: async (childId: number, imageData: string): Promise<ChildAvatarUploadResponse> => {
    try {
      const payload: AvatarUploadRequest = { imageData };
      const response = await api.post<ChildAvatarUploadResponse>(`/api/family/children/${childId}/avatar`, payload);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Provide specific error messages based on status code and response
        const responseData = error.response?.data as { message?: string } | undefined;
        if (error.response?.status === 403) {
          throw new Error('You do not have permission to edit this child\'s avatar. Only adult family members and administrators can edit child avatars.');
        } else if (error.response?.status === 404) {
          throw new Error('Child not found. The child may have been removed or you may not have access to this family.');
        } else if (error.response?.status === 400) {
          const serverMessage = responseData?.message;
          if (serverMessage?.includes('image')) {
            throw new Error('Invalid image format. Please try uploading a different image.');
          }
          throw new Error(serverMessage || 'Invalid request. Please check your input and try again.');
        }
        const errorMessage = responseData?.message || 'Failed to upload child avatar. Please try again.';
        throw new Error(errorMessage);
      }
      throw error;
    }
  },

  /**
   * Removes the avatar for a child
   * Issue #137 - Child avatar management with primary caregiver permissions
   * @param childId - The ID of the child to update
   * @returns Promise containing success message
   * @throws Error if user lacks permission or request fails
   */
  deleteChildAvatar: async (childId: number): Promise<ChildAvatarDeleteResponse> => {
    try {
      const response = await api.delete<ChildAvatarDeleteResponse>(`/api/family/children/${childId}/avatar`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Provide specific error messages based on status code and response
        const responseData = error.response?.data as { message?: string } | undefined;
        if (error.response?.status === 403) {
          throw new Error('You do not have permission to remove this child\'s avatar. Only adult family members and administrators can edit child avatars.');
        } else if (error.response?.status === 404) {
          throw new Error('Child not found or child has no avatar to remove. The child may have been removed or you may not have access to this family.');
        }
        const errorMessage = responseData?.message || 'Failed to remove child avatar. Please try again.';
        throw new Error(errorMessage);
      }
      throw error;
    }
  },

  // Alexa Integration Management - Issue #11
  getAlexaIntegrationStatus: async (): Promise<AlexaIntegrationStatusDto> => {
    try {
      const response = await api.get<AlexaIntegrationStatusDto>('/api/family/alexa-status');
      return response.data;
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      throw new Error(errorMessage || 'Failed to get Alexa integration status');
    }
  },

  unlinkAlexaIntegration: async (): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>('/api/family/alexa-unlink');
      return response.data;
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      throw new Error(errorMessage || 'Failed to unlink Alexa integration');
    }
  },

  getAlexaOAuthConfig: async (): Promise<{ clientId: string; authorizationUrl: string; redirectUri: string }> => {
    try {
      const response = await api.get<{ clientId: string; authorizationUrl: string; redirectUri: string }>('/api/family/alexa-oauth-config');
      return response.data;
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      throw new Error(errorMessage || 'Failed to get OAuth configuration');
    }
  },

  completeAlexaOAuthFlow: async (request: { authCode: string }): Promise<{ redirectUri: string }> => {
    try {
      const response = await api.post<{ redirectUri: string }>('/api/auth/oauth/alexa/complete', request);
      return response.data;
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      throw new Error(errorMessage || 'Failed to complete Alexa OAuth flow');
    }
  },

  // Voice Integration Management - Issue #1145
  getUserVoiceIntegrations: async (): Promise<UserVoiceIntegrationDto[]> => {
    try {
      const response = await api.get<UserVoiceIntegrationDto[]>('/api/user/voice-integrations');
      return response.data;
    } catch (error: unknown) {
      const errorMessage = (axios.isAxiosError(error) ? error.response?.data?.message : undefined) || 'Failed to get voice integrations';
      throw new Error(errorMessage);
    }
  },

  unlinkVoiceIntegration: async (integrationType: string): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>(`/api/user/voice-integrations/${integrationType}/unlink`);
      return response.data;
    } catch (error: unknown) {
      const errorMessage = (axios.isAxiosError(error) ? error.response?.data?.message : undefined) || 'Failed to unlink voice integration';
      throw new Error(errorMessage);
    }
  },

  getFamilyVoiceIntegrations: async (familyId: number): Promise<FamilyVoiceIntegrationStatusDto[]> => {
    try {
      const response = await api.get<FamilyVoiceIntegrationStatusDto[]>(`/api/family/${familyId}/voice-integrations`);
      return response.data;
    } catch (error: unknown) {
      const errorMessage = (axios.isAxiosError(error) ? error.response?.data?.message : undefined) || 'Failed to get family voice integrations';
      throw new Error(errorMessage);
    }
  },

  connectVoiceIntegrationToFamily: async (familyId: number, integrationType: string): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>(`/api/family/${familyId}/voice-integrations/connect`, { integrationType });
      return response.data;
    } catch (error: unknown) {
      const errorMessage = (axios.isAxiosError(error) ? error.response?.data?.message : undefined) || 'Failed to connect voice integration to family';
      throw new Error(errorMessage);
    }
  },

  disconnectVoiceIntegrationFromFamily: async (familyId: number, userVoiceIntegrationId: number): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>(`/api/family/${familyId}/voice-integrations/disconnect`, { userVoiceIntegrationId });
      return response.data;
    } catch (error: unknown) {
      const errorMessage = (axios.isAxiosError(error) ? error.response?.data?.message : undefined) || 'Failed to disconnect voice integration from family';
      throw new Error(errorMessage);
    }
  },

  /**
   * Gets family analytics data including Who is Logging metrics
   */
  // New API method for Issue #833 - Today's Recording Progress Card  
  getEffectiveDailyLoggingGoal: async (familyId: number): Promise<number> => {
    const response = await api.get<number>(`/api/family/${familyId}/analytics/effective-daily-logging-goal`);
    return response.data;
  },

  getAnalytics: async (familyId: number, params: {
    metrics: string[];
    timeWindow?: string;
    startDate?: string;
    endDate?: string;
    familyTimezone?: string; // Add family timezone parameter
  }): Promise<FamilyAnalyticsResponse> => {
    // Calculate date range based on timeWindow if not provided
    let startDate = params.startDate;
    let endDate = params.endDate;

    if (!startDate || !endDate) {
      // Use timezone-aware utilities instead of browser timezone
      const timeWindow = params.timeWindow || 'Last 7 days';
      const dateRange = getDateRangeFromTimeWindow(timeWindow, params.familyTimezone);

      startDate = dateRange.startDate;
      endDate = dateRange.endDate;
    }

    const queryParams = new URLSearchParams({
      metrics: params.metrics.join(','),
      timeWindow: params.timeWindow || 'Last 7 days',
      startDate: startDate!,
      endDate: endDate!
    });

    const response = await api.get<FamilyAnalyticsResponse>(`/api/family/${familyId}/analytics?${queryParams}`);
    return response.data;
  },

  /**
   * Gets pending invitations for a family
   * Returns list of invitations that haven't been accepted yet
   */
  getFamilyInvitations: async (familyId: number): Promise<InvitationDto[]> => {
    const response = await api.get<InvitationDto[]>(`/api/family/${familyId}/invitations`);
    return response.data;
  },

  /**
   * Resends an invitation email to a pending invitation
   * @param invitationId - The ID of the invitation to resend
   */
  resendInvitation: async (invitationId: number): Promise<void> => {
    await api.post(`/api/family/invitations/${invitationId}/resend`);
  },

  /**
   * Cancels a pending invitation
   * @param invitationId - The ID of the invitation to cancel
   */
  cancelInvitation: async (invitationId: number): Promise<void> => {
    await api.delete<void>(`/api/family/invitations/${invitationId}`);
  },

  /**
   * Updates family settings (daily logging goal and/or theme). PUT /api/family/settings
   * always overwrites both fields from the request body, so callers must send the
   * complete current state — not just the field being changed — or the omitted field
   * gets cleared to null.
   * @param settings - The full settings state to persist
   */
  updateFamilySettings: async (settings: { dailyLoggingGoalPerChild: number | null; themeName: string | null; labelLanguageMode: string | null }): Promise<void> => {
    await api.put('/api/family/settings', settings);
  },
};

// Per-child Crossing Steersman preference (Issue #1940) -- parent surface (JWT).
export const getCrossingChildPreferences = async (childId: number): Promise<CrossingChildPreferencesDto> => {
  const response = await api.get<CrossingChildPreferencesDto>(`/api/children/${childId}/crossing/preferences`);
  return response.data;
};

export const setSteersmanEnabled = async (childId: number, steersmanEnabled: boolean): Promise<CrossingChildPreferencesDto> => {
  const response = await api.put<CrossingChildPreferencesDto>(`/api/children/${childId}/crossing/preferences`, { steersmanEnabled });
  return response.data;
};

export const incrementSteersmanSeen = async (crossingId: number): Promise<CrossingChildPreferencesDto> => {
  const response = await api.post<CrossingChildPreferencesDto>(`/api/crossings/${crossingId}/steersman/seen`);
  return response.data;
};

// Per-child Crossing Steersman preference (Issue #1940) -- child surface (slug + token).
export const getCrossingChildPreferencesForChild = async (slug: string, token?: string): Promise<CrossingChildPreferencesDto> => {
  const response = await api.get<CrossingChildPreferencesDto>(`/api/child/${slug}/crossing/preferences`, { params: token ? { token } : {} });
  return response.data;
};

export const setSteersmanEnabledForChild = async (slug: string, steersmanEnabled: boolean, token?: string): Promise<CrossingChildPreferencesDto> => {
  const response = await api.put<CrossingChildPreferencesDto>(
    `/api/child/${slug}/crossing/preferences`,
    { steersmanEnabled },
    { params: token ? { token } : {} }
  );
  return response.data;
};

export const incrementSteersmanSeenForChild = async (slug: string, crossingId: number, token?: string): Promise<CrossingChildPreferencesDto> => {
  const response = await api.post<CrossingChildPreferencesDto>(
    `/api/child/${slug}/crossings/${crossingId}/steersman/seen`,
    undefined,
    { params: token ? { token } : {} }
  );
  return response.data;
};

// Behavior API
export const behaviorAPI = {
  recordBehavior: async (childId: number, behaviorType: 'positive' | 'warning' | 'negative' | BehaviorType, note?: string): Promise<RecordBehaviorResponse> => {
    let behaviorTypeValue: number;

    // Handle both old string-based types and new BehaviorType enum
    if (typeof behaviorType === 'string') {
      const typeMap = {
        'positive': 1,
        'warning': 2,
        'negative': 3
      };
      behaviorTypeValue = typeMap[behaviorType];
    } else {
      // Direct BehaviorType enum value (1=Star, 2=Triangle, 3=Square, 4=FlipYourCoin)
      behaviorTypeValue = behaviorType;
    }

    const data: Omit<RecordBehaviorRequest, 'childId'> = {
      type: behaviorTypeValue as BehaviorType,
      notes: note
    };

    const response = await api.post<RecordBehaviorResponse>(`/api/Behavior/${childId}/record`, data);
    return response.data;
  },

  getBehaviors: async (childId: number): Promise<BehaviorRecord[]> => {
    const response = await api.get<RawBehaviorDto[]>(`/api/Behavior/child/${childId}`);
    return response.data.map(behavior => ({
      ...behavior,
      behaviorId: behavior.id || behavior.behaviorId,
      behaviorType: behavior.type === 1 || behavior.behaviorType === 1 ? BehaviorType.Star :
                    behavior.type === 2 || behavior.behaviorType === 2 ? BehaviorType.Triangle :
                    behavior.type === 3 || behavior.behaviorType === 3 ? BehaviorType.Square :
                    behavior.type === 4 || behavior.behaviorType === 4 ? BehaviorType.FlipYourCoin :
                    behavior.type === 5 || behavior.behaviorType === 5 ? BehaviorType.MakeItRight :
                    behavior.type === 6 || behavior.behaviorType === 6 ? BehaviorType.OneOnOneTime :
                    BehaviorType.Square,
      timestamp: behavior.recordedAt,
      recordedByUserName: behavior.recordedByUserName || 'Unknown User'
    })) as BehaviorRecord[];
  },

  getChildBehaviorsByDate: async (childId: number, date: string, browserTimezone?: string): Promise<BehaviorRecord[]> => {
    const params = new URLSearchParams();
    params.append('date', date);
    if (browserTimezone) params.append('browserTimezone', browserTimezone);

    const response = await api.get<RawBehaviorDto[]>(`/api/Behavior/child/${childId}?${params.toString()}`);
    return response.data.map((behavior) => ({
      ...behavior,
      behaviorId: behavior.id || behavior.behaviorId,
      behaviorType: behavior.type === 1 || behavior.behaviorType === 1 ? BehaviorType.Star :
                    behavior.type === 2 || behavior.behaviorType === 2 ? BehaviorType.Triangle :
                    behavior.type === 3 || behavior.behaviorType === 3 ? BehaviorType.Square :
                    behavior.type === 4 || behavior.behaviorType === 4 ? BehaviorType.FlipYourCoin :
                    behavior.type === 5 || behavior.behaviorType === 5 ? BehaviorType.MakeItRight :
                    behavior.type === 6 || behavior.behaviorType === 6 ? BehaviorType.OneOnOneTime :
                    BehaviorType.MakeItRight,
      recordedByUserName: behavior.recordedByUserName || 'Unknown User',
      // Enhanced time fields for proper timezone handling
      eventTime: behavior.eventTime || behavior.recordedAt,
      eventTimezone: behavior.eventTimezone,
      eventLocalTime: behavior.eventLocalTime
    })) as BehaviorRecord[];
  },

  updateBehavior: async (behaviorId: number, behaviorType: BehaviorType, notes?: string, childId?: number, newEventTime?: string): Promise<BehaviorRecord> => {
    const data = {
      type: behaviorType,
      notes: notes || '',
      childId: childId,
      NewEventTime: newEventTime  // Backend expects 'NewEventTime' field
    };
    const response = await api.put<RawBehaviorDto>(`/api/Behavior/${behaviorId}`, data);
    const responseData = response.data;
    return {
      ...responseData,
      behaviorId: responseData.id ?? responseData.behaviorId ?? 0,
      behaviorType: responseData.type === 1 || responseData.behaviorType === 1 ? BehaviorType.Star :
                    responseData.type === 2 || responseData.behaviorType === 2 ? BehaviorType.Triangle :
                    BehaviorType.Square,
      recordedAt: responseData.recordedAt ?? '',
      recordedByUserId: responseData.recordedByUserId ?? 0,
      recordedByUserName: responseData.recordedByUserName || 'Unknown User',
      // Enhanced time fields (Issue #312 and #371)
      eventTime: responseData.eventTime,
      eventLocalTime: responseData.eventLocalTime,
      eventTimezone: responseData.eventTimezone
    };
  },

  deleteBehavior: async (behaviorId: number): Promise<void> => {
    await api.delete<void>(`/api/Behavior/${behaviorId}`);
  },

  getDailyProgress: async (childId: number, date?: string): Promise<DailyProgress> => {
    // If no date is provided, let the backend determine "today" using the family's timezone
    const url = date 
      ? `/api/Behavior/progress/child/${childId}/daily?date=${date}`
      : `/api/Behavior/progress/child/${childId}/daily`;
    const response = await api.get<DailyProgress>(url);
    return response.data;
  },

  getLadderHistory: async (childId: number, days: number = 7): Promise<DailyProgress[]> => {
    // Let backend calculate dates using family timezone - pass days parameter instead of calculated dates
    const response = await api.get<DailyProgress[]>(`/api/behavior/progress/child/${childId}/history?days=${days}`);
    return response.data;
  },

  getChildHeartDates: async (childId: number, startDate: Date, endDate: Date, familyTimezone?: string): Promise<{ heartDates: string[]; mendingHeartDates: string[] }> => {
    const startDateParam = getDateInFamilyTimezone(startDate, familyTimezone);
    const endDateParam = getDateInFamilyTimezone(endDate, familyTimezone);
    const response = await api.get<{ heartDates: string[]; mendingHeartDates: string[] }>(`/api/Behavior/child/${childId}/hearts?startDate=${startDateParam}&endDate=${endDateParam}`);
    return response.data;
  },

  getChildProgressHistorySummary: async (childId: number, startDate: Date, endDate: Date, familyTimezone?: string): Promise<DailyProgress[]> => {
    const startDateParam = getDateInFamilyTimezone(startDate, familyTimezone);
    const endDateParam = getDateInFamilyTimezone(endDate, familyTimezone);
    const response = await api.get<DailyProgress[]>(`/api/behavior/progress/child/${childId}/history?startDate=${startDateParam}&endDate=${endDateParam}&includeBehaviors=false`);
    return response.data;
  },

  getBehaviorRecords: async (
    familyId: number,
    startDate?: string,
    endDate?: string,
    childId?: number | null,
    behaviorTypes?: string[],
    days?: number,
    page?: number,
    pageSize?: number,
    paginate?: boolean
  ): Promise<PagedBehaviorResponse> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (childId !== null && childId !== undefined) params.append('childId', childId.toString());
    if (behaviorTypes && behaviorTypes.length > 0) {
      behaviorTypes.forEach(type => params.append('behaviorTypes', type));
    }
    if (days !== undefined) params.append('days', days.toString());
    if (page !== undefined) params.append('page', page.toString());
    if (pageSize !== undefined) params.append('pageSize', pageSize.toString());
    if (paginate !== undefined) params.append('paginate', paginate.toString());

    const response = await api.get<RawBehaviorDto[] | PagedApiResponse<RawBehaviorDto>>(`/api/Behavior/family/${familyId}?${params.toString()}`);

    // Handle both old format (array) and new format (paged response) for backward compatibility (Issue #1081)
    if (Array.isArray(response.data)) {
      // Legacy format - convert to paged format
      const behaviors = response.data.map(record => ({
        behaviorId: record.id ?? record.behaviorId ?? 0,
        childId: record.childId,
        behaviorType: record.type ?? record.behaviorType ?? 0,
        notes: record.notes,
        recordedAt: record.createdAt ?? '',
        recordedByUserId: record.recordedByUserId ?? 0,
        recordedByUserName: record.recordedByUserName,
        source: record.source,
        sourceDisplayName: record.sourceDisplayName,
        eventTime: record.eventTime,
        eventTimezone: record.eventTimezone,
        eventLocalTime: record.eventLocalTime
      }));

      return {
        data: behaviors,
        page: 1,
        pageSize: behaviors.length,
        totalCount: behaviors.length,
        totalPages: 1
      };
    }

    // New paged format (Issue #1081)
    const pagedData = response.data as PagedApiResponse<RawBehaviorDto>;
    return {
      data: pagedData.data.map((record) => ({
        behaviorId: record.id ?? record.behaviorId ?? 0,
        childId: record.childId,
        behaviorType: record.type ?? record.behaviorType ?? 0,
        notes: record.notes,
        recordedAt: record.createdAt ?? '',
        recordedByUserId: record.recordedByUserId ?? 0,
        recordedByUserName: record.recordedByUserName,
        source: record.source,
        sourceDisplayName: record.sourceDisplayName,
        eventTime: record.eventTime,
        eventTimezone: record.eventTimezone,
        eventLocalTime: record.eventLocalTime
      })),
      page: pagedData.page,
      pageSize: pagedData.pageSize,
      totalCount: pagedData.totalCount,
      totalPages: pagedData.totalPages
    };
  },

  getTodaysLoggingCount: async (familyId: number): Promise<number> => {
    const response = await api.get<number>(`/api/Family/${familyId}/analytics/todays-logging-count`);
    return response.data;
  },

  getTodaysBehaviorCountsByType: async (familyId: number, date?: string): Promise<Record<number, number>> => {
    const url = date 
      ? `/api/Family/${familyId}/analytics/todays-counts-by-type?date=${date}`
      : `/api/Family/${familyId}/analytics/todays-counts-by-type`;
    const response = await api.get<Record<number, number>>(url);
    return response.data;
  },

  getBehaviorCountsByTypeForPeriod: async (familyId: number, startDate?: string, endDate?: string): Promise<Record<number, number>> => {
    let url = `/api/Family/${familyId}/analytics/behavior-counts-by-type`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await api.get<Record<number, number>>(url);
    return response.data;
  },

  getFamilyDevelopmentalAverages: async (familyId: number): Promise<Record<number, number>> => {
    const response = await api.get<Record<number, number>>(`/api/Family/${familyId}/analytics/developmental-averages`);
    return response.data;
  },

  getFamilyDevelopmentalAveragesForChild: async (familyId: number, childId: number): Promise<Record<number, number>> => {
    const response = await api.get<Record<number, number>>(`/api/Family/${familyId}/analytics/developmental-averages?childId=${childId}`);
    return response.data;
  },

  getLastBehaviorLogTime: async (familyId: number): Promise<string | null> => {
    const response = await api.get<{ lastLoggedTime: string | null }>(`/api/Family/${familyId}/analytics/last-log-time`);
    return response.data.lastLoggedTime;
  },

  // New API methods for Issue #833 - Today's Recording Progress Card
  getTodaysBehaviorCountsByChild: async (familyId: number, options?: { timezone?: string }): Promise<Record<number, number>> => {
    const params = new URLSearchParams();
    if (options?.timezone) {
      params.append('timezone', options.timezone);
    }

    const url = `/api/family/${familyId}/analytics/todays-counts-by-child${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<Record<number, number>>(url);
    return response.data;
  },

  /**
   * Gets meltdown tracker data for a child (Issue #958)
   * @param childId - The child ID to get meltdown data for
   */
  getMeltdownTrackerData: async (childId: number): Promise<MeltdownTrackerDto> => {
    const response = await api.get<MeltdownTrackerDto>(`/api/behavior/meltdown-tracker/child/${childId}`);
    return response.data;
  },

  /**
   * Gets triangle (struggles) tracker data for a child
   * @param childId - The child ID to get triangle data for
   */
  getTriangleTrackerData: async (childId: number): Promise<TriangleTrackerDto> => {
    const response = await api.get<TriangleTrackerDto>(`/api/behavior/triangle-tracker/child/${childId}`);
    return response.data;
  },

  /**
   * Gets one-on-one time tracker data for a child (Issue #1065)
   * @param childId - The child ID to get 1:1 time data for
   */
  getOneOnOneTimeTrackerData: async (childId: number): Promise<OneOnOneTimeTrackerDto> => {
    const response = await api.get<OneOnOneTimeTrackerDto>(`/api/behavior/one-on-one-time-tracker/child/${childId}`);
    return response.data;
  },
};

// Goals API
export const goalsAPI = {
  getChildGoals: async (childId: number): Promise<Goal[]> => {
    const response = await api.get<Goal[]>(`/api/goal/child/${childId}`);
    // Transform backend GoalDto format to frontend Goal format
    return response.data.map(goal => ({
      ...goal,
      currentProgress: goal.progress?.currentValue ?? 0,
    }));
  },

  createGoal: async (goal: CreateGoalRequest): Promise<Goal | Goal[]> => {
    const response = await api.post<Goal | Goal[]>('/api/goal', goal);
    return response.data;
  },

  getGoal: async (goalId: number): Promise<Goal> => {
    const response = await api.get<Goal>(`/api/goal/${goalId}`);
    return response.data;
  },

  updateGoal: async (goalId: number, updates: Partial<Goal>): Promise<Goal> => {
    const response = await api.put<Goal>(`/api/goal/${goalId}`, updates);
    return response.data;
  },

  deleteGoal: async (goalId: number): Promise<void> => {
    await api.delete<void>(`/api/goal/${goalId}`);
  },

  getFamilyGoals: async (familyId: number): Promise<Goal[]> => {
    const response = await api.get<PagedApiResponse<Goal>>(`/api/goal/family/${familyId}?paginate=false`);
    const pagedData = response.data;
    return (pagedData.data || []).map((goal) => ({
      ...goal,
      currentProgress: goal.progress?.currentValue ?? goal.currentProgress ?? 0,
      isRecurring: goal.isRecurring ?? false
    })) as Goal[];
  },

  getFamilyGoalsPaginated: async (
    familyId: number,
    options?: {
      page?: number;
      pageSize?: number;
      filterStatus?: string;
      childId?: number | null;
      goalTypes?: number[];
      isRecurring?: boolean;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Promise<PagedGoalResponse> => {
    const params = new URLSearchParams();
    params.append('paginate', 'true');
    if (options?.page !== undefined) params.append('page', options.page.toString());
    if (options?.pageSize !== undefined) params.append('pageSize', options.pageSize.toString());
    if (options?.filterStatus) params.append('filterStatus', options.filterStatus);
    if (options?.childId !== null && options?.childId !== undefined) params.append('childId', options.childId.toString());
    if (options?.goalTypes && options.goalTypes.length > 0) {
      options.goalTypes.forEach(t => params.append('goalTypes', t.toString()));
    }
    if (options?.isRecurring !== undefined) params.append('isRecurring', options.isRecurring.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

    const response = await api.get<PagedApiResponse<Goal>>(`/api/goal/family/${familyId}?${params.toString()}`);
    const pagedData = response.data;

    return {
      ...pagedData,
      data: (pagedData.data || []).map((goal) => ({
        ...goal,
        currentProgress: goal.progress?.currentValue ?? goal.currentProgress ?? 0,
        isRecurring: goal.isRecurring ?? false
      }))
    } as PagedGoalResponse;
  },

  uploadGoalImage: async (goalId: number, imageData: string): Promise<void> => {
    await api.post(`/api/goal/${goalId}/image`, { imageData });
  },

  removeGoalImage: async (goalId: number): Promise<void> => {
    await api.delete<void>(`/api/goal/${goalId}/image`);
  },

  celebrateGoal: async (goalId: number): Promise<Goal> => {
    const response = await api.post<Goal>(`/api/goal/${goalId}/celebrate`);
    return response.data;
  },

  duplicateGoal: async (goalId: number): Promise<Goal> => {
    const response = await api.post<Goal>(`/api/goal/${goalId}/duplicate`);
    return response.data;
  },

  // Admin-specific goal creation endpoint
  adminCreateGoal: async (familyId: number, goal: CreateGoalRequest): Promise<Goal | Goal[]> => {
    const response = await api.post<Goal | Goal[]>(`/api/admin/families/${familyId}/goals`, goal);
    return response.data;
  },

  // Admin-specific family reprocess endpoint (recalculates hearts with chore checks, then goals)
  adminReprocessFamily: async (familyId: number): Promise<GoalRecalculationResultDto> => {
    const response = await api.post<GoalRecalculationResultDto>(`/api/admin/families/${familyId}/reprocess`);
    return response.data;
  },

  // Backward compatibility alias
  adminRecalculateFamilyGoals: async (familyId: number): Promise<GoalRecalculationResultDto> => {
    const response = await api.post<GoalRecalculationResultDto>(`/api/admin/families/${familyId}/reprocess`);
    return response.data;
  },

  // Nuclear reset: fails all active non-exempt goals for a child and recreates them at zero (Issue #1310)
  nukeChildGoals: async (childId: number): Promise<NukeResult> => {
    const response = await api.post<NukeResult>(`/api/goal/child/${childId}/nuke`);
    return response.data;
  }
};

// Role API - Role suggestions and user role management
export const roleAPI = {
  getRoleSuggestions: async (): Promise<RoleSuggestion[]> => {
    const response = await api.get<RoleSuggestion[]>('/api/role/suggestions');
    return response.data;
  },

  updateUserRole: async (userId: number, familyId: number, role: string): Promise<void> => {
    await api.put<void>(`/api/role/users/${userId}/families/${familyId}/role`, { role });
  },

  adminCreateRole: async (request: CreateRoleSuggestionRequest): Promise<RoleSuggestion> => {
    const response = await api.post<RoleSuggestion>('/api/role/admin/roles', request);
    return response.data;
  },

  adminUpdateRole: async (roleId: number, request: UpdateRoleSuggestionRequest): Promise<RoleSuggestion> => {
    const response = await api.put<RoleSuggestion>(`/api/role/admin/roles/${roleId}`, request);
    return response.data;
  },

  adminDeleteRole: async (roleId: number): Promise<void> => {
    await api.delete<void>(`/api/role/admin/roles/${roleId}`);
  },

  adminUpdateUserRole: async (userId: number, familyId: number, role: string): Promise<void> => {
    await api.put<void>(`/api/role/admin/users/${userId}/families/${familyId}/role`, { role });
  },

  adminUpdateFamilyRole: async (userId: number, familyId: number, customRole: string, isPrimaryCaregiver: boolean): Promise<void> => {
    await api.put<void>(`/api/role/admin/users/${userId}/families/${familyId}/family-role`, {
      customRole,
      isPrimaryCaregiver
    });
  },
};

// Chores API - Issue #1308
export const choresAPI = {
  // Get all chore lists for a family
  getChoreListsForFamily: async (familyId: number): Promise<ChoreList[]> => {
    const response = await api.get<ChoreList[]>(`/api/family/${familyId}/responsibilities`);
    return response.data;
  },

  // Get all chore lists for a specific child, optionally scoped to a historical date
  getChoreListsForChild: async (familyId: number, childId: number, date?: string): Promise<ChoreList[]> => {
    const url = date
      ? `/api/family/${familyId}/children/${childId}/responsibilities?date=${date}`
      : `/api/family/${familyId}/children/${childId}/responsibilities`;
    const response = await api.get<ChoreList[]>(url);
    return response.data;
  },

  // Create a new chore list
  createChoreList: async (familyId: number, childId: number, request: CreateChoreListRequest): Promise<ChoreList> => {
    const response = await api.post<ChoreList>(`/api/family/${familyId}/children/${childId}/responsibilities`, request);
    return response.data;
  },

  // Get a specific chore list
  getChoreList: async (choreListId: number): Promise<ChoreList> => {
    const response = await api.get<ChoreList>(`/api/responsibilities/${choreListId}`);
    return response.data;
  },

  // Update a chore list
  updateChoreList: async (choreListId: number, request: UpdateChoreListRequest): Promise<ChoreList> => {
    const response = await api.put<ChoreList>(`/api/responsibilities/${choreListId}`, request);
    return response.data;
  },

  // Delete a chore list
  deleteChoreList: async (choreListId: number): Promise<void> => {
    await api.delete(`/api/responsibilities/${choreListId}`);
  },

  // Skip a daily chore list for today (not reversible)
  skipChoreListToday: async (choreListId: number): Promise<void> => {
    await api.post(`/api/responsibilities/${choreListId}/skip-today`);
  },

  unskipChoreListToday: async (choreListId: number): Promise<void> => {
    await api.delete(`/api/responsibilities/${choreListId}/skip-today`);
  },

  retireChoreList: async (choreListId: number): Promise<void> => {
    await api.post(`/api/responsibilities/${choreListId}/retire`);
  },

  // Mark an item as complete
  markItemComplete: async (choreListId: number, itemId: number): Promise<MarkItemCompleteResponse> => {
    const response = await api.post<MarkItemCompleteResponse>(`/api/responsibilities/${choreListId}/items/${itemId}/complete`);
    return response.data;
  },

  // Unmark an item as complete
  unmarkItemComplete: async (choreListId: number, itemId: number): Promise<void> => {
    await api.delete(`/api/responsibilities/${choreListId}/items/${itemId}/complete`);
  },

  // Alias for unmarkItemComplete for consistency
  markItemIncomplete: async (choreListId: number, itemId: number): Promise<void> => {
    await api.delete(`/api/responsibilities/${choreListId}/items/${itemId}/complete`);
  },

  // Get chore list status for current period, or historical status if date is provided (YYYY-MM-DD)
  getChoreListStatus: async (choreListId: number, date?: string): Promise<ChoreListPeriodStatus> => {
    const url = date
      ? `/api/responsibilities/${choreListId}/status?date=${date}`
      : `/api/responsibilities/${choreListId}/status`;
    const response = await api.get<ChoreListPeriodStatus>(url);
    return response.data;
  },

  // Get all chore list statuses for a child (for child dashboard), optionally for a historical date
  getChoreListStatuses: async (familyId: number, childId: number, date?: string): Promise<ChoreListPeriodStatus[]> => {
    // Get all chore lists for the child (scoped to date if provided), then fetch status for each
    const choreLists = await choresAPI.getChoreListsForChild(familyId, childId, date);
    const results = await Promise.all(
      choreLists.map(async (list): Promise<ChoreListPeriodStatus | null> => {
        try {
          return await choresAPI.getChoreListStatus(list.id, date);
        } catch (err: unknown) {
          // When fetching historical status, backend returns 404 for MakeItRight/ResetAndReflect
          // lists (they are excluded from historical view). Omit them rather than showing stale data.
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (date && status === 404) {
            return null;
          }
          // For non-404 errors (or current-period fetches), return a minimal fallback status
          return {
            id: list.id,
            title: list.title,
            repeatFrequency: list.repeatFrequency,
            requiredForHeart: list.requiredForHeart,
            items: list.items.map(item => ({
              id: item.id,
              title: item.title,
              sortOrder: item.sortOrder,
              isCompleted: false,
              completedAt: undefined,
            })),
            isComplete: false,
            completedItems: 0,
            totalItems: list.items.length,
            periodStart: new Date().toISOString(),
            periodEnd: new Date().toISOString(),
          } as ChoreListPeriodStatus;
        }
      })
    );
    return results.filter((s): s is ChoreListPeriodStatus => s !== null);
  },

  // Get chore list completion history
  getChoreListHistory: async (choreListId: number, page: number = 1, pageSize: number = 20): Promise<ChoreHistoryResponse> => {
    const response = await api.get<ChoreHistoryResponse>(`/api/responsibilities/${choreListId}/history?page=${page}&pageSize=${pageSize}`);
    return response.data;
  },

  // Get family chores summary (for parent dashboard)
  getFamilyChoresSummary: async (familyId: number): Promise<FamilyChoresSummary> => {
    const response = await api.get<FamilyChoresSummary>(`/api/family/${familyId}/responsibilities/summary`);
    return response.data;
  },

  // Update the display order of Regular chore lists for a child
  updateChoreListOrder: async (familyId: number, childId: number, orderedIds: number[]): Promise<void> => {
    await api.put(`/api/family/${familyId}/children/${childId}/responsibilities/order`, { orderedIds });
  },

  // Approve a Make It Right chore list
  approveMakeItRightList: async (choreListId: number): Promise<void> => {
    await api.post(`/api/chores/${choreListId}/approve`);
  },
};

// Make It Right Template API - Issue #1379
export const makeItRightTemplateAPI = {
  // Get child template
  getChildTemplate: async (childId: number): Promise<MakeItRightTemplate> => {
    const response = await api.get<MakeItRightTemplate>(`/api/children/${childId}/make-it-right-template`);
    return response.data;
  },

  // Update child template
  updateChildTemplate: async (
    childId: number,
    request: UpdateMakeItRightTemplateRequest
  ): Promise<MakeItRightTemplate> => {
    const response = await api.put<MakeItRightTemplate>(`/api/children/${childId}/make-it-right-template`, request);
    return response.data;
  },

  // Restore defaults
  restoreDefaults: async (childId: number, currentTasks: string[]): Promise<MakeItRightTemplate> => {
    const response = await api.post<MakeItRightTemplate>(
      `/api/children/${childId}/make-it-right-template/restore-defaults`,
      { tasks: currentTasks }
    );
    return response.data;
  },

  // Get site defaults (admin only)
  getSiteDefaults: async (): Promise<SiteDefaultMakeItRightTasks> => {
    const response = await api.get<SiteDefaultMakeItRightTasks>('/api/admin/make-it-right-defaults');
    return response.data;
  },

  // Update site defaults (admin only)
  updateSiteDefaults: async (request: SiteDefaultMakeItRightTasks): Promise<void> => {
    await api.put('/api/admin/make-it-right-defaults', request);
  },
};

// Reset and Reflect Template API - Issue #1694
export const resetAndReflectTemplateAPI = {
  // Get child template
  getChildTemplate: async (childId: number): Promise<ResetAndReflectTemplate> => {
    const response = await api.get<ResetAndReflectTemplate>(`/api/children/${childId}/reset-and-reflect-template`);
    return response.data;
  },

  // Update child template
  updateChildTemplate: async (
    childId: number,
    request: UpdateResetAndReflectTemplateRequest
  ): Promise<ResetAndReflectTemplate> => {
    const response = await api.put<ResetAndReflectTemplate>(`/api/children/${childId}/reset-and-reflect-template`, request);
    return response.data;
  },

  // Restore defaults
  restoreDefaults: async (childId: number, currentTasks: string[]): Promise<ResetAndReflectTemplate> => {
    const response = await api.post<ResetAndReflectTemplate>(
      `/api/children/${childId}/reset-and-reflect-template/restore-defaults`,
      { tasks: currentTasks }
    );
    return response.data;
  },

  // Get site defaults (admin only)
  getSiteDefaults: async (): Promise<SiteDefaultResetAndReflectTasks> => {
    const response = await api.get<SiteDefaultResetAndReflectTasks>('/api/admin/reset-and-reflect-defaults');
    return response.data;
  },

  // Update site defaults (admin only)
  updateSiteDefaults: async (request: SiteDefaultResetAndReflectTasks): Promise<void> => {
    await api.put('/api/admin/reset-and-reflect-defaults', request);
  },
};

export const oneOnOneTimeTemplateAPI = {
  // Get child template
  getChildTemplate: async (childId: number): Promise<OneOnOneTimeTemplate> => {
    const response = await api.get<OneOnOneTimeTemplate>(`/api/children/${childId}/one-on-one-time-template`);
    return response.data;
  },

  // Update child template
  updateChildTemplate: async (
    childId: number,
    request: UpdateOneOnOneTimeTemplateRequest
  ): Promise<OneOnOneTimeTemplate> => {
    const response = await api.put<OneOnOneTimeTemplate>(`/api/children/${childId}/one-on-one-time-template`, request);
    return response.data;
  },

  // Restore defaults
  restoreDefaults: async (childId: number, currentTasks: string[]): Promise<OneOnOneTimeTemplate> => {
    const response = await api.post<OneOnOneTimeTemplate>(
      `/api/children/${childId}/one-on-one-time-template/restore-defaults`,
      { tasks: currentTasks }
    );
    return response.data;
  },

  // Get site defaults (admin only)
  getSiteDefaults: async (): Promise<SiteDefaultOneOnOneTimeTasks> => {
    const response = await api.get<SiteDefaultOneOnOneTimeTasks>('/api/admin/one-on-one-time-defaults');
    return response.data;
  },

  // Update site defaults (admin only)
  updateSiteDefaults: async (request: SiteDefaultOneOnOneTimeTasks): Promise<void> => {
    await api.put('/api/admin/one-on-one-time-defaults', request);
  },
};

// Admin API - User Management
export const adminAPI = {
  getUsers: async (params: {
    page: number;
    pageSize: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    isAdmin?: boolean;
    sortBy?: string;
    sortDirection?: string;
    showDeleted?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());

    // Combine search filters into searchTerm as backend expects
    const searchTerms = [];
    if (params.firstName) searchTerms.push(params.firstName);
    if (params.lastName) searchTerms.push(params.lastName);
    if (params.email) searchTerms.push(params.email);

    if (searchTerms.length > 0) {
      queryParams.append('searchTerm', searchTerms.join(' '));
    }

    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDirection === 'desc') queryParams.append('sortDescending', 'true');
    if (params.showDeleted) queryParams.append('showDeleted', params.showDeleted);

    const response = await api.get(`/api/admin/users?${queryParams.toString()}`);

    // Map new PagedResponse format to old format for backward compatibility
    const responseData = response.data;
    return {
      items: responseData.data,
      pageNumber: responseData.page,
      totalCount: responseData.totalCount,
      pageSize: responseData.pageSize,
      totalPages: responseData.totalPages
    };
  },

  getUserDetail: async (userId: number) => {
    if (userId <= 0) {
      throw new Error('Invalid user ID');
    }
    const response = await api.get(`/api/admin/users/${userId}`);
    return response.data;
  },

  updateUser: async (userId: number, userData: {
    firstName: string;
    lastName: string;
    email: string;
    profileImageUrl?: string;
    role?: string;
    mantra?: string;
    familyLoveQuestion?: string;
    familyNeedQuestion?: string;
    isAdmin: boolean;
  }) => {
    const response = await api.put(`/api/admin/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId: number) => {
    const response = await api.delete(`/api/admin/users/${userId}`);
    return response.data;
  },

  resetUserPassword: async (userId: number, passwordData: {
    newPassword: string;
    confirmPassword: string;
  }) => {
    const response = await api.post(`/api/admin/users/${userId}/reset-password`, passwordData);
    return response.data;
  },

  getAllChildren: async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: string;
    searchTerm?: string;
    showDeleted?: string;
  }) => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    });
    
    if (params.searchTerm) {
      queryParams.append('searchTerm', params.searchTerm);
    }
    if (params.showDeleted) {
      queryParams.append('showDeleted', params.showDeleted);
    }
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
      queryParams.append('sortDesc', params.sortOrder === 'desc' ? 'true' : 'false');
    }

    type AdminChildItem = { id: number; name: string; age: number; familyId: number; familyName: string; lastBehaviorRecord: string; lastBehaviorEventTime: string; lastBehaviorEventLocalTime: string; familyMemberNames: string; createdAt: string; isDeleted: boolean };
    const response = await api.get<PagedApiResponse<AdminChildItem>>(`/api/admin/children?${queryParams.toString()}`);
    const responseData = response.data;

    // Transform response to match frontend expectations
    return {
      children: responseData.data.map((child) => ({
        id: child.id,
        childId: child.id,
        name: child.name,
        age: child.age,
        familyId: child.familyId,
        familyName: child.familyName,
        lastBehaviorRecord: child.lastBehaviorRecord,
        lastBehaviorEventTime: child.lastBehaviorEventTime,
        lastBehaviorEventLocalTime: child.lastBehaviorEventLocalTime,
        primaryParentNames: child.familyMemberNames || 'Not Available',
        createdAt: child.createdAt,
        isActive: true,
        isDeleted: child.isDeleted || false
      })),
      totalCount: responseData.totalCount,
      totalPages: responseData.totalPages
    };
  },

  getChildDetail: async (childId: string) => {
    const response = await api.get(`/api/admin/children/${childId}`);
    return response.data;
  },

  getChildBehaviorLog: async (childId: string, params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    });
    
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }
    if (params.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate);
    }

    const response = await api.get(`/api/admin/children/${childId}/behavior-log?${queryParams.toString()}`);

    // Map new PagedResponse format to old format for backward compatibility
    const responseData = response.data;
    return {
      events: responseData.data || [],
      totalCount: responseData.totalCount || 0,
      page: responseData.page || 1,
      pageSize: responseData.pageSize || params.pageSize,
      totalPages: responseData.totalPages || 0
    };
  },

  getAdminStats: async () => {
    const response = await api.get('/api/admin/stats');
    return response.data;
  },

  getSubscriptionMetrics: async () => {
    const response = await api.get('/api/admin/subscriptions/metrics');
    return response.data;
  },

  getFamilyUsageAnalytics: async (period?: number) => {
    const queryParams = period ? `?period=${period}` : '';
    const response = await api.get(`/api/admin/usage-analytics${queryParams}`);
    return response.data;
  },

  getDailyEngagementTrends: async (period: number = 30) => {
    const response = await api.get(`/api/admin/usage-analytics/daily-trends?period=${period}`);
    return response.data;
  },

  getFamilyAnalytics: async (familyId: number, period: number = 30) => {
    const response = await api.get(`/api/admin/families/${familyId}/analytics?period=${period}`);
    return response.data;
  },

  getBehaviorPatterns: async (familyId: number, timezone?: string) => {
    const params = new URLSearchParams();
    if (timezone) {
      params.append('timezone', timezone);
    }
    const response = await api.get(`/api/family/${familyId}/analytics/behavior-patterns-by-time?${params.toString()}`);
    return response.data;
  },

  getBehaviorPatternsByDayOfWeek: async (familyId: number, timezone?: string) => {
    const params = new URLSearchParams();
    if (timezone) {
      params.append('timezone', timezone);
    }
    const response = await api.get(`/api/family/${familyId}/analytics/behavior-patterns-by-day-of-week?${params.toString()}`);
    return response.data;
  },

  getAdminBehaviors: async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: string;
    familyNameFilter?: string;
    childNameFilter?: string;
    notesFilter?: string;
    showDeleted?: string;
  }) => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    });
    
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.showDeleted) queryParams.append('showDeleted', params.showDeleted);
    if (params.familyNameFilter) queryParams.append('familyNameFilter', params.familyNameFilter);
    if (params.childNameFilter) queryParams.append('childNameFilter', params.childNameFilter);
    if (params.notesFilter) queryParams.append('notesFilter', params.notesFilter);

    const response = await api.get(`/api/admin/behaviors/all?${queryParams.toString()}`);

    // Transform the response to match frontend expectations
    return {
      behaviors: response.data.data,
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalPages: response.data.totalPages
    };
  },

  updateAdminBehavior: async (behaviorId: number, data: {
    behaviorType: number;
    notes?: string;
    childId?: number;
  }) => {
    const response = await api.put(`/api/admin/behaviors/${behaviorId}`, data);
    return response.data;
  },

  deleteAdminBehavior: async (behaviorId: number) => {
    const response = await api.delete(`/api/admin/behaviors/${behaviorId}`);
    return response.data;
  },

  // Family management methods
  getAllFamilies: async (page: number, pageSize: number, search?: string, sortBy?: string, sortDesc?: boolean, showDeleted?: string, subscriptionStatus?: string) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (search) queryParams.append('search', search);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortDesc) queryParams.append('sortDesc', sortDesc.toString());
    if (showDeleted) queryParams.append('showDeleted', showDeleted);
    if (subscriptionStatus) queryParams.append('subscriptionStatus', subscriptionStatus);

    const response = await api.get(`/api/admin/families?${queryParams.toString()}`);
    const data = response.data;

    // Transform to match frontend expectations
    return {
      families: data.data,
      totalCount: data.totalCount,
      page: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages
    };
  },

  getFamilyById: async (familyId: number) => {
    const response = await api.get(`/api/admin/families/${familyId}`);
    return response.data;
  },

  getFamilySubscriptionStatus: async (familyId: number) => {
    const response = await api.get(`/api/admin/families/${familyId}/subscription-status`);
    return response.data;
  },

  // Subscription management (Issue #1381)
  editFreeAccess: async (familyId: number, reason: string, expiresAt: string) => {
    const response = await api.put(`/api/admin/subscriptions/${familyId}/free-access`, { reason, expiresAt });
    return response.data;
  },

  extendTrial: async (familyId: number, days: number, reason: string) => {
    const response = await api.post(`/api/admin/subscriptions/${familyId}/extend-trial`, { days, reason });
    return response.data;
  },

  restartTrial: async (familyId: number, reason: string) => {
    const response = await api.post(`/api/admin/subscriptions/${familyId}/restart-trial`, { reason });
    return response.data;
  },

  forceExpire: async (familyId: number, reason: string) => {
    const response = await api.post(`/api/admin/subscriptions/${familyId}/force-expire`, { reason });
    return response.data;
  },

  emergencyLockout: async (familyId: number, reason: string) => {
    const response = await api.post(`/api/admin/subscriptions/${familyId}/emergency-lockout`, { reason });
    return response.data;
  },

  removeLockout: async (familyId: number, reason: string) => {
    const response = await api.post(`/api/admin/subscriptions/${familyId}/remove-lockout`, { reason });
    return response.data;
  },

  getSubscriptionHistory: async (familyId: number, page: number, pageSize: number) => {
    const response = await api.get(`/api/admin/subscriptions/${familyId}/history`, {
      params: { page, pageSize }
    });
    return response.data;
  },

  addSubscriptionNote: async (familyId: number, noteText: string) => {
    const response = await api.post(`/api/admin/subscriptions/${familyId}/notes`, { noteText });
    return response.data;
  },

  updateFamily: async (familyId: number, data: {
    name: string;
    description?: string;
    timezone?: string;
  }) => {
    const response = await api.put(`/api/admin/families/${familyId}`, data);
    return response.data;
  },

  getFamilyBehaviors: async (familyId: number, page: number, pageSize: number, childId?: number | null, behaviorTypes?: string[]) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (childId !== null && childId !== undefined) {
      queryParams.append('childId', childId.toString());
    }

    if (behaviorTypes && behaviorTypes.length > 0) {
      behaviorTypes.forEach(type => {
        queryParams.append('behaviorTypes', type);
      });
    }

    const response = await api.get(`/api/admin/families/${familyId}/behaviors?${queryParams.toString()}`);
    const data = response.data;

    // Transform to match frontend expectations
    return {
      behaviors: data.data,
      totalCount: data.totalCount,
      page: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages
    };
  },

  // Policy Management
  getPolicyDocument: async (documentType: PolicyDocumentType): Promise<PolicyDocument> => {
    const endpoint = documentType === 'PrivacyPolicy' ? '/api/policy/admin/privacy' : '/api/policy/admin/terms';
    const response = await api.get<PolicyDocument>(endpoint);
    return response.data;
  },

  updatePolicyDocument: async (documentType: PolicyDocumentType, updateData: UpdatePolicyRequest): Promise<PolicyDocument> => {
    const endpoint = documentType === 'PrivacyPolicy' ? '/api/policy/admin/privacy' : '/api/policy/admin/terms';
    const response = await api.put<PolicyDocument>(endpoint, updateData);
    return response.data;
  },

  getPolicyHistory: async (documentType: PolicyDocumentType): Promise<PolicyDocumentHistory[]> => {
    const response = await api.get<PolicyDocumentHistory[]>(`/api/policy/admin/${documentType}/history`);
    return response.data;
  },

  getPolicyAcknowledgments: async (params: {
    pageNumber?: number;
    pageSize?: number;
    documentType?: PolicyDocumentType;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedPolicyAcknowledgments> => {
    const queryParams = new URLSearchParams();
    if (params.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.documentType) queryParams.append('documentType', params.documentType);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get<PaginatedPolicyAcknowledgments>(`/api/policy/admin/acknowledgments?${queryParams}`);
    return response.data;
  },

  getUserPolicyAcknowledgments: async (userId: number): Promise<UserPolicyAcknowledgment[]> => {
    const response = await api.get<UserPolicyAcknowledgment[]>(`/api/policy/admin/acknowledgments/user/${userId}`);
    return response.data;
  },

  // Backup Management
  createBackup: async () => {
    const response = await api.post('/api/admin/backup');
    return response.data;
  },

  getBackupStatus: async () => {
    const response = await api.get('/api/admin/backup/status');
    return response.data;
  },

  getScheduledBackupStatus: async () => {
    const response = await api.get('/api/admin/backup/scheduled/status');
    return response.data;
  },

  getBackupHistory: async (page: number = 1, pageSize: number = 10) => {
    const response = await api.get(`/api/admin/backup/history?page=${page}&pageSize=${pageSize}`);
    return response.data;
  },

  deleteBackup: async (filename: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/api/admin/backup/${filename}`);
    return response.data;
  },

  downloadBackup: async (filename: string): Promise<Blob> => {
    const response = await api.get(`/api/admin/backup/${filename}/download`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  },

  // Audit Log Management
  getAuditLogs: async (params: {
    page?: number;
    pageSize?: number;
    entityType?: string;
    action?: string;
    performedBy?: number;
    fromDate?: string;
    toDate?: string;
    search?: string;
  }): Promise<{
    items: Array<{
      id: number;
      entityType: string;
      entityId: string;
      action: string;
      performedBy: number;
      performedByUserName: string;
      performedAt: string;
      details: string | null;
    }>;
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params.page || 1).toString());
    queryParams.append('pageSize', (params.pageSize || 25).toString());

    if (params.entityType) queryParams.append('entityType', params.entityType);
    if (params.action) queryParams.append('action', params.action);
    if (params.performedBy) queryParams.append('performedBy', params.performedBy.toString());
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.search) queryParams.append('search', params.search);

    const response = await api.get<{
      data: Array<{
        id: number;
        entityType: string;
        entityId: string;
        action: string;
        performedBy: number;
        performedByUserName: string;
        performedAt: string;
        details: string | null;
      }>;
      totalCount: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/api/admin/audit?${queryParams.toString()}`);

    // Return with consistent property names for backward compatibility
    return {
      items: response.data.data,
      totalCount: response.data.totalCount,
      pageNumber: response.data.page,
      pageSize: response.data.pageSize,
      totalPages: response.data.totalPages
    };
  },

  exportAuditLogs: async (params: {
    format: string;
    entityType?: string;
    action?: string;
    performedBy?: number;
    fromDate?: string;
    toDate?: string;
    search?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    queryParams.append('format', params.format);
    
    if (params.entityType) queryParams.append('entityType', params.entityType);
    if (params.action) queryParams.append('action', params.action);
    if (params.performedBy) queryParams.append('performedBy', params.performedBy.toString());
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.search) queryParams.append('search', params.search);

    const response = await api.get(`/api/admin/audit/export?${queryParams.toString()}`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  },

  // Enhanced Audit Detail for Issue #505
  getAuditLogDetails: async (auditLogId: number): Promise<{
    id: number;
    entityType: string;
    entityId: string;
    action: string;
    performedBy: number;
    performedByUserName: string;
    performedByUserRole?: string;
    performedAt: string;
    details?: string;
    oldValues?: string;
    newValues?: string;
    entityDisplayName?: string;
    familyName?: string;
    familyTimezone?: string;
    fieldChanges?: Array<{
      fieldName: string;
      oldValue?: string;
      newValue?: string;
      oldDisplayValue?: string;
      newDisplayValue?: string;
    }>;
  }> => {
    const response = await api.get<{
      id: number;
      entityType: string;
      entityId: string;
      action: string;
      performedBy: number;
      performedByUserName: string;
      performedByUserRole?: string;
      performedAt: string;
      details?: string;
      oldValues?: string;
      newValues?: string;
      entityDisplayName?: string;
      familyName?: string;
      familyTimezone?: string;
      fieldChanges?: Array<{
        fieldName: string;
        oldValue?: string;
        newValue?: string;
        oldDisplayValue?: string;
        newDisplayValue?: string;
      }>;
    }>(`/api/admin/audit/${auditLogId}/details`);
    return response.data;
  },

  // Developmental Averages Management
  getDevelopmentalAveragesAdmin: async (): Promise<DevelopmentalAverageDto[]> => {
    const response = await api.get<DevelopmentalAverageDto[]>('/api/admin/developmental-averages');
    return response.data;
  },

  updateDevelopmentalAverages: async (averages: DevelopmentalAverageDto[]): Promise<DevelopmentalAverageDto[]> => {
    const response = await api.put<DevelopmentalAverageDto[]>('/api/admin/developmental-averages', {
      averages: averages
    });
    return response.data;
  },
};

// Export convenience functions for backwards compatibility
export const getUsers = adminAPI.getUsers;
export const getUserDetail = adminAPI.getUserDetail;
export const updateUser = adminAPI.updateUser;
export const deleteUser = adminAPI.deleteUser;
export const getAllChildren = adminAPI.getAllChildren;
export const getChildDetail = adminAPI.getChildDetail;
export const getChildBehaviorLog = adminAPI.getChildBehaviorLog;
export const getAdminStats = adminAPI.getAdminStats;
export const getSubscriptionMetrics = adminAPI.getSubscriptionMetrics;
export const getFamilyUsageAnalytics = adminAPI.getFamilyUsageAnalytics;
export const getDailyEngagementTrends = adminAPI.getDailyEngagementTrends;
export const getFamilyAnalytics = adminAPI.getFamilyAnalytics;
export const getBehaviorPatterns = adminAPI.getBehaviorPatterns;
export const getBehaviorPatternsByDayOfWeek = adminAPI.getBehaviorPatternsByDayOfWeek;
export const getAdminBehaviors = adminAPI.getAdminBehaviors;
export const getAllFamilies = adminAPI.getAllFamilies;
export const getFamilyById = adminAPI.getFamilyById;
export const getFamilySubscriptionStatus = adminAPI.getFamilySubscriptionStatus;
export const updateFamily = adminAPI.updateFamily;
export const getFamilyBehaviors = adminAPI.getFamilyBehaviors;
export const createBackup = adminAPI.createBackup;
export const getBackupStatus = adminAPI.getBackupStatus;
export const getScheduledBackupStatus = adminAPI.getScheduledBackupStatus;
export const getBackupHistory = adminAPI.getBackupHistory;
export const deleteBackup = adminAPI.deleteBackup;
export const downloadBackup = adminAPI.downloadBackup;
export const getAdminAuditLogs = adminAPI.getAuditLogs;
export const exportAdminAuditLogs = adminAPI.exportAuditLogs;
export const getAdminAuditLogDetails = adminAPI.getAuditLogDetails;

// Admin Webhook API
interface WebhookEventDto {
  id: number;
  eventType: string;
  lemonSqueezyEventId: string | null;
  familyId: number | null;
  familyName: string | null;
  subscriptionId: number | null;
  payload: string;
  status: string;
  errorMessage: string | null;
  retryCount: number;
  processedAt: string | null;
  createdAt: string;
}

interface WebhookListResponse {
  data: WebhookEventDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface RetryWebhookResponse {
  success: boolean;
  message: string;
  webhookEvent: WebhookEventDto;
}

export const getAdminWebhooks = async (params?: {
  page?: number;
  pageSize?: number;
  eventTypes?: string[];
  statuses?: string[];
  fromDate?: string;
  toDate?: string;
  familyId?: number;
  search?: string;
}): Promise<WebhookListResponse> => {
  const response = await api.get<WebhookListResponse>('/api/admin/webhooks', { params });
  return response.data;
};

export const retryWebhook = async (webhookId: number): Promise<RetryWebhookResponse> => {
  const response = await api.post<RetryWebhookResponse>(`/api/admin/webhooks/${webhookId}/retry`);
  return response.data;
};

// User Profile API - for current user's profile management
export const getCurrentUserProfile = async () => {
  const response = await api.get('/api/user/profile');
  return response.data;
};

export const updateCurrentUserProfile = async (profileData: {
  firstName: string;
  lastName: string;
  email: string;
  mantra?: string;
  question1Answer?: string;
  question2Answer?: string;
  question3Answer?: string;
}) => {
  // Map to backend DTO format
  const dto = {
    firstName: profileData.firstName,
    lastName: profileData.lastName,
    email: profileData.email,
    mantra: profileData.mantra,
    familyLoveQuestion: profileData.question1Answer,
    familyNeedQuestion: profileData.question2Answer,
    // Note: question3Answer is not supported by backend yet
  };
  const response = await api.put('/api/user/profile', dto);
  return response.data;
};

export const changeCurrentUserPassword = async (currentPassword: string, newPassword: string) => {
  const response = await api.post('/api/user/change-password', {
    currentPassword,
    newPassword,
    confirmNewPassword: newPassword
  });
  return response.data;
};

// Auth Provider Management API functions
export const getUserAuthProviders = async (): Promise<UserAuthProvider[]> => {
  const response = await api.get('/api/user/auth-providers');
  return response.data as UserAuthProvider[];
};

export const removeAuthProvider = async (authProvider: string): Promise<{ message: string }> => {
  const response = await api.request({
    method: 'DELETE',
    url: '/api/user/auth-providers',
    data: { authProvider }
  });
  return response.data as unknown as { message: string };
};

export const checkCanRemoveAuthProvider = async (authProvider: string): Promise<CanRemoveAuthProviderResponse> => {
  const response = await api.get<CanRemoveAuthProviderResponse>(`/api/user/auth-providers/${authProvider}/can-remove`);
  return response.data as CanRemoveAuthProviderResponse;
};

// Policy Management API functions
export const adminGetPrivacyPolicy = (): Promise<PolicyDocument> => 
  adminAPI.getPolicyDocument('PrivacyPolicy');

export const adminGetTermsOfService = (): Promise<PolicyDocument> => 
  adminAPI.getPolicyDocument('TermsOfService');

export const adminUpdatePrivacyPolicy = (updateData: UpdatePolicyRequest): Promise<PolicyDocument> => 
  adminAPI.updatePolicyDocument('PrivacyPolicy', updateData);

export const adminUpdateTermsOfService = (updateData: UpdatePolicyRequest): Promise<PolicyDocument> => 
  adminAPI.updatePolicyDocument('TermsOfService', updateData);

export const adminGetPolicyHistory = (documentType: PolicyDocumentType): Promise<PolicyDocumentHistory[]> => 
  adminAPI.getPolicyHistory(documentType);

export const adminGetPolicyAcknowledgments = (params: {
  pageNumber?: number;
  pageSize?: number;
  documentType?: PolicyDocumentType;
  startDate?: string;
  endDate?: string;
}): Promise<PaginatedPolicyAcknowledgments> => 
  adminAPI.getPolicyAcknowledgments(params);

export const adminGetUserPolicyAcknowledgments = (userId: number): Promise<UserPolicyAcknowledgment[]> => 
  adminAPI.getUserPolicyAcknowledgments(userId);

// Public policy API functions (for registration)
export const getPrivacyPolicy = async (): Promise<PolicyDocument> => {
  const response = await api.get<PolicyDocument>('/api/policy/privacy');
  return response.data;
};

export const getTermsOfService = async (): Promise<PolicyDocument> => {
  const response = await api.get<PolicyDocument>('/api/policy/terms');
  return response.data;
};

export const acknowledgePolicyDocument = async (acknowledgment: PolicyAcknowledgmentRequest): Promise<void> => {
  await api.post('/api/policy/acknowledge', acknowledgment);
};

export const checkUserPolicyAcceptance = async (): Promise<{
  requiresAcceptance: boolean;
  lastAcceptedVersions: {
    privacyPolicy: number | null;
    termsOfService: number | null;
  };
  currentVersions: {
    privacyPolicy: number;
    termsOfService: number;
  };
}> => {
  const response = await api.get<{
    requiresAcceptance: boolean;
    lastAcceptedVersions: {
      privacyPolicy: number | null;
      termsOfService: number | null;
    };
    currentVersions: {
      privacyPolicy: number;
      termsOfService: number;
    };
  }>('/api/policy/check-acceptance');
  return response.data;
};

// OAuth API functions
export const completeAlexaOAuthFlow = async (request: { authCode: string }): Promise<{ redirectUri: string }> => {
  return familyAPI.completeAlexaOAuthFlow(request);
};

// Password Reset API functions
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  return authAPI.forgotPassword(email);
};

export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  return authAPI.resetPassword(token, newPassword);
};

// Email Verification API functions (Issue #986)
export const verifyEmail = async (token: string): Promise<{ message: string }> => {
  return authAPI.verifyEmail(token);
};

export const resendVerificationEmail = async (email: string): Promise<{ message: string }> => {
  return authAPI.resendVerificationEmail(email);
};

// Analytics API functions
export const analyticsAPI = {
  async getWeeklyRecording(familyId: number, timezone?: string): Promise<{
    currentWeekTotal: number;
    weeklyGoal: number;
    previousWeekTotal: number | null;
    percentageComplete: number;
    trend: {
      direction: 'up' | 'down' | 'same' | 'first';
      displayValue: string;
    };
    weekHistory: {
      weekStartDate: string;
      total: number;
    }[];
  }> {
    const params = new URLSearchParams();
    params.append('familyId', familyId.toString());
    if (timezone) {
      params.append('timezone', timezone);
    }
    
    const response = await api.get<{
      currentWeekTotal: number;
      weeklyGoal: number;
      previousWeekTotal: number | null;
      percentageComplete: number;
      trend: {
        direction: 'up' | 'down' | 'same' | 'first';
        displayValue: string;
      };
      weekHistory: {
        weekStartDate: string;
        total: number;
      }[];
    }>(`/api/family/${familyId}/analytics/weekly-recording?${params.toString()}`);
    return response.data;
  },

  async getBehaviorPatternsByTime(familyId: number, timezone?: string, childId?: number): Promise<{
    hourlyAverages: {
      hour: number;
      stars: number;
      triangles: number;
      squares: number;
      flipYourCoins: number;
      makeItRights: number;
      oneOnOneTimes: number;
      total: number;
    }[];
    dateRange: {
      startDate: string;
      endDate: string;
    };
    totalDays: number;
    totalBehaviors: number;
  }> {
    const params = new URLSearchParams();
    params.append('familyId', familyId.toString());
    if (timezone) {
      params.append('timezone', timezone);
    }
    if (childId) {
      params.append('childId', childId.toString());
    }

    const response = await api.get<{
      hourlyAverages: {
        hour: number;
        stars: number;
        triangles: number;
        squares: number;
        flipYourCoins: number;
        makeItRights: number;
        oneOnOneTimes: number;
        total: number;
      }[];
      dateRange: {
        startDate: string;
        endDate: string;
      };
      totalDays: number;
      totalBehaviors: number;
    }>(`/api/family/${familyId}/analytics/behavior-patterns-by-time?${params.toString()}`);
    return response.data;
  },

  async getBehaviorPatternsByDayOfWeek(familyId: number, timezone?: string, childId?: number): Promise<{
    dayOfWeekAverages: {
      dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
      stars: number;
      triangles: number;
      squares: number;
      flipYourCoins: number;
      makeItRights: number;
      oneOnOneTimes: number;
      total: number;
    }[];
    dateRange: {
      startDate: string;
      endDate: string;
    };
    totalDays: number;
    totalBehaviors: number;
  }> {
    const params = new URLSearchParams();
    params.append('familyId', familyId.toString());
    if (timezone) {
      params.append('timezone', timezone);
    }
    if (childId) {
      params.append('childId', childId.toString());
    }

    const response = await api.get<{
      dayOfWeekAverages: {
        dayOfWeek: number;
        stars: number;
        triangles: number;
        squares: number;
        flipYourCoins: number;
        makeItRights: number;
        oneOnOneTimes: number;
        total: number;
      }[];
      dateRange: {
        startDate: string;
        endDate: string;
      };
      totalDays: number;
      totalBehaviors: number;
    }>(`/api/family/${familyId}/analytics/behavior-patterns-by-day-of-week?${params.toString()}`);
    return response.data;
  },

  async getPositivityRatioOverTime(
    familyId: number,
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'month',
    familyTimezone?: string,
    childId?: number
  ): Promise<Array<{
    period: string;
    starCount: number;
    negativeCount: number;
    ratio: number | null;
    isMaxPositive: boolean;
  }>> {
    const params = new URLSearchParams({ startDate, endDate, granularity });
    if (familyTimezone) params.append('familyTimezone', familyTimezone);
    if (childId) params.append('childId', childId.toString());
    const response = await api.get<Array<{
      period: string;
      starCount: number;
      negativeCount: number;
      ratio: number | null;
      isMaxPositive: boolean;
    }>>(`/api/family-insights/${familyId}/positivity-ratio?${params.toString()}`);
    return response.data;
  },

  async getBehaviorBreakdownOverTime(
    familyId: number,
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'month',
    familyTimezone?: string,
    childId?: number
  ): Promise<Array<{
    period: string;
    stars: number;
    triangles: number;
    squares: number;
    flipYourCoins: number;
    makeItRights: number;
    oneOnOneTimes: number;
  }>> {
    const params = new URLSearchParams({ startDate, endDate, granularity });
    if (familyTimezone) params.append('familyTimezone', familyTimezone);
    if (childId) params.append('childId', childId.toString());
    const response = await api.get<Array<{
      period: string;
      stars: number;
      triangles: number;
      squares: number;
      flipYourCoins: number;
      makeItRights: number;
      oneOnOneTimes: number;
    }>>(`/api/family-insights/${familyId}/behavior-breakdown?${params.toString()}`);
    return response.data;
  },
};

/**
 * Subscription API endpoints for LemonSqueezy integration
 * Issue #1026, #1029, & #1036 - Trial and subscription management
 */
export const subscriptionAPI = {
  /**
   * Get current user's family subscription status
   * No parameters needed - uses authenticated user's family
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    const response = await api.get<SubscriptionStatusResponse>('/api/subscription/status');
    return response.data;
  },

  /**
   * Get LemonSqueezy customer portal URL for billing management
   * Only accessible by billing owner
   * Issue #1029
   */
  async getCustomerPortalUrl(): Promise<{ url: string }> {
    const response = await api.get<{ url: string }>('/api/subscription/customer-portal');
    return response.data;
  },

  /**
   * Create LemonSqueezy checkout session for subscription purchase
   * Issue #1030
   * @param familyId - The family ID to associate with the subscription
   * @param email - The user's email to pre-fill in checkout
   * @returns The checkout URL to open in overlay
   */
  async createCheckout(familyId: number, email: string): Promise<CreateCheckoutResponse> {
    const request: CreateCheckoutRequest = { email };
    const response = await api.post<CreateCheckoutResponse>(`/api/subscription/${familyId}/create-checkout`, request);
    return response.data;
  },

  /**
   * Cancel the current family subscription (billing owner only)
   * Maintains access until current period ends (grace period)
   * Issue #1034-C
   */
  async cancelSubscription(): Promise<SubscriptionStatusResponse> {
    const response = await api.post<SubscriptionStatusResponse>('/api/subscription/cancel');
    return response.data;
  },
};

// ============================================================================
// IMPERSONATION API
// ============================================================================

export interface ImpersonationResponse {
  userId: number;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
}

export interface ValidationResponse {
  isValid: boolean;
}

export interface ImpersonationStatusResponse {
  isImpersonating: boolean;
  impersonatedUser: {
    userId: number;
    email: string;
    fullName: string;
    firstName: string;
    lastName: string;
    profileImageUrl: string | null;
  } | null;
}

export const startImpersonation = async (userId: number): Promise<ImpersonationResponse> => {
  const response = await api.post<ImpersonationResponse>('/api/admin/impersonation/start', { userId });
  return response.data;
};

export const endImpersonation = async (userId: number): Promise<void> => {
  await api.post('/api/admin/impersonation/end', { userId });
};

export const validateImpersonatedUser = async (userId: number): Promise<ValidationResponse> => {
  const response = await api.get<ValidationResponse>(`/api/admin/impersonation/validate/${userId}`);
  return response.data;
};

export const getImpersonationStatus = async (): Promise<ImpersonationStatusResponse> => {
  const response = await api.get<ImpersonationStatusResponse>('/api/admin/impersonation/status');
  return response.data;
};

// ============================================================================
// KNOWLEDGE BASE API
// ============================================================================

// Category operations
export const getKBCategories = async (): Promise<import('../types/knowledgeBase').KBCategoryDto[]> => {
  const response = await api.get<import('../types/knowledgeBase').KBCategoryDto[]>('/api/knowledgebase/categories');
  return response.data;
};

export const createKBCategory = async (dto: import('../types/knowledgeBase').CreateKBCategoryDto): Promise<import('../types/knowledgeBase').KBCategoryDto> => {
  const response = await api.post<import('../types/knowledgeBase').KBCategoryDto>('/api/knowledgebase/categories', dto);
  return response.data;
};

export const updateKBCategory = async (id: number, dto: import('../types/knowledgeBase').UpdateKBCategoryDto): Promise<import('../types/knowledgeBase').KBCategoryDto> => {
  const response = await api.put<import('../types/knowledgeBase').KBCategoryDto>(`/api/knowledgebase/categories/${id}`, dto);
  return response.data;
};

export const deleteKBCategory = async (id: number): Promise<void> => {
  await api.delete<void>(`/api/knowledgebase/categories/${id}`);
};

export const reorderKBCategories = async (dto: import('../types/knowledgeBase').ReorderCategoriesDto): Promise<void> => {
  await api.post<void>('/api/knowledgebase/categories/reorder', dto);
};

// Tag operations
export const getKBTags = async (): Promise<import('../types/knowledgeBase').KBTagDto[]> => {
  const response = await api.get<import('../types/knowledgeBase').KBTagDto[]>('/api/knowledgebase/tags');
  return response.data;
};

export const createKBTag = async (name: string): Promise<import('../types/knowledgeBase').KBTagDto> => {
  const response = await api.post<import('../types/knowledgeBase').KBTagDto>('/api/knowledgebase/tags', { name });
  return response.data;
};

export const deleteKBTag = async (id: number): Promise<void> => {
  await api.delete<void>(`/api/knowledgebase/tags/${id}`);
};

// Article operations
export const getKBArticles = async (): Promise<import('../types/knowledgeBase').KBArticleListDto[]> => {
  const response = await api.get<import('../types/knowledgeBase').KBArticleListDto[]>('/api/knowledgebase/articles');
  return response.data;
};

export const getKBArticle = async (id: number): Promise<import('../types/knowledgeBase').KBArticleDto> => {
  const response = await api.get<import('../types/knowledgeBase').KBArticleDto>(`/api/knowledgebase/articles/${id}`);
  return response.data;
};

export const createKBArticle = async (dto: import('../types/knowledgeBase').CreateKBArticleDto): Promise<import('../types/knowledgeBase').KBArticleDto> => {
  const response = await api.post<import('../types/knowledgeBase').KBArticleDto>('/api/knowledgebase/articles', dto);
  return response.data;
};

export const updateKBArticle = async (id: number, dto: import('../types/knowledgeBase').UpdateKBArticleDto): Promise<import('../types/knowledgeBase').KBArticleDto> => {
  const response = await api.put<import('../types/knowledgeBase').KBArticleDto>(`/api/knowledgebase/articles/${id}`, dto);
  return response.data;
};

export const deleteKBArticle = async (id: number): Promise<void> => {
  await api.delete<void>(`/api/knowledgebase/articles/${id}`);
};

// Video operations
export const getKBVideos = async (): Promise<import('../types/knowledgeBase').KBVideoDto[]> => {
  const response = await api.get<import('../types/knowledgeBase').KBVideoDto[]>('/api/knowledgebase/videos');
  return response.data;
};

export const getKBVideo = async (id: number): Promise<import('../types/knowledgeBase').KBVideoDto> => {
  const response = await api.get<import('../types/knowledgeBase').KBVideoDto>(`/api/knowledgebase/videos/${id}`);
  return response.data;
};

export const createKBVideo = async (dto: import('../types/knowledgeBase').CreateKBVideoDto): Promise<import('../types/knowledgeBase').KBVideoDto> => {
  const response = await api.post<import('../types/knowledgeBase').KBVideoDto>('/api/knowledgebase/videos', dto);
  return response.data;
};

export const updateKBVideo = async (id: number, dto: import('../types/knowledgeBase').UpdateKBVideoDto): Promise<import('../types/knowledgeBase').KBVideoDto> => {
  const response = await api.put<import('../types/knowledgeBase').KBVideoDto>(`/api/knowledgebase/videos/${id}`, dto);
  return response.data;
};

export const deleteKBVideo = async (id: number): Promise<void> => {
  await api.delete<void>(`/api/knowledgebase/videos/${id}`);
};

export const updateVideoEngagement = async (dto: import('../types/knowledgeBase').UpdateVideoEngagementDto): Promise<import('../types/knowledgeBase').VideoEngagementDto> => {
  const response = await api.post<import('../types/knowledgeBase').VideoEngagementDto>('/api/knowledgebase/videos/engagement', dto);
  return response.data;
};

// Related content
export const getRelatedArticles = async (articleId: number): Promise<KBArticleListDto[]> => {
  const response = await api.get<KBArticleListDto[]>(`/api/knowledgebase/articles/${articleId}/related`);
  return response.data;
};

export const getRelatedVideos = async (videoId: number): Promise<KBVideoDto[]> => {
  const response = await api.get<KBVideoDto[]>(`/api/knowledgebase/videos/${videoId}/related`);
  return response.data;
};

// Search
export const searchKnowledgeBase = async (query: string): Promise<import('../types/knowledgeBase').KBSearchResultsDto> => {
  const response = await api.get<import('../types/knowledgeBase').KBSearchResultsDto>('/api/knowledgebase/search', { params: { query: query } });
  return response.data;
};

// Calendar Integration API
export const calendarAPI = {
  // Connection management
  getConnections: async (): Promise<CalendarConnectionDto[]> => {
    const response = await api.get<CalendarConnectionDto[]>('/api/calendar/connections');
    return response.data;
  },
  createConnection: async (dto: CreateCalendarConnectionDto): Promise<CalendarConnectionDto> => {
    const response = await api.post<CalendarConnectionDto>('/api/calendar/connections', dto);
    return response.data;
  },
  deleteConnection: async (connectionId: number): Promise<void> => {
    await api.delete(`/api/calendar/connections/${connectionId}`);
  },
  startOAuth: async (providerType: CalendarProviderType, redirectUri: string): Promise<CalendarOAuthStartDto> => {
    const response = await api.post<CalendarOAuthStartDto>('/api/calendar/connections/oauth/start', { providerType, redirectUri });
    return response.data;
  },
  refreshSources: async (connectionId: number): Promise<CalendarSourceDto[]> => {
    const response = await api.post<CalendarSourceDto[]>(`/api/calendar/connections/${connectionId}/refresh-sources`);
    return response.data;
  },

  // Child calendar selections
  getChildSelections: async (childId: number): Promise<ChildCalendarSelectionDto> => {
    const response = await api.get<ChildCalendarSelectionDto>(`/api/calendar/children/${childId}/selections`);
    return response.data;
  },
  updateChildSelections: async (childId: number, calendarSourceIds: number[]): Promise<ChildCalendarSelectionDto> => {
    const response = await api.put<ChildCalendarSelectionDto>(`/api/calendar/children/${childId}/selections`, { calendarSourceIds });
    return response.data;
  },

  // Events
  getChildEvents: async (childId: number, date: string, timezone?: string): Promise<ChildCalendarEventsDto> => {
    const params = new URLSearchParams({ date });
    if (timezone) params.append('timezone', timezone);
    const response = await api.get<ChildCalendarEventsDto>(`/api/calendar/children/${childId}/events?${params}`);
    return response.data;
  },

  // Health check
  checkConnectionHealth: async (connectionId: number): Promise<CalendarConnectionHealthDto> => {
    const response = await api.get<CalendarConnectionHealthDto>(`/api/calendar/connections/${connectionId}/health`);
    return response.data;
  },

  // CalDAV credential update (reconnect after 401/403)
  updateCalDavCredentials: async (connectionId: number, dto: UpdateCalDavCredentialsDto): Promise<CalendarConnectionDto> => {
    const response = await api.put<CalendarConnectionDto>(`/api/calendar/connections/${connectionId}/credentials`, dto);
    return response.data;
  },
};

/**
 * Insights API endpoints for FamilyInsightsPage trend data
 * Issue #1606 — uses FamilyInsightsController from #1605
 */
export const insightsAPI = {
  async getHeartDayRateOverTime(
    familyId: number,
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'month',
    childId?: number,
    familyTimezone?: string,
  ): Promise<{ period: string; heartDayCount: number; totalDays: number; successRate: number }[]> {
    const params = new URLSearchParams({ startDate, endDate, granularity });
    if (childId !== undefined) params.append('childId', childId.toString());
    if (familyTimezone) params.append('familyTimezone', familyTimezone);
    const response = await api.get<{ period: string; heartDayCount: number; totalDays: number; successRate: number }[]>(
      `/api/family-insights/${familyId}/heart-day-rate?${params.toString()}`
    );
    return response.data;
  },

  async getAllTimeAverageHeartDayRate(
    familyId: number,
    childId?: number,
    familyTimezone?: string,
  ): Promise<number> {
    const params = new URLSearchParams();
    if (childId !== undefined) params.append('childId', childId.toString());
    if (familyTimezone) params.append('familyTimezone', familyTimezone);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get<{ allTimeAverageSuccessRate: number }>(
      `/api/family-insights/${familyId}/heart-day-rate/all-time-average${query}`
    );
    return response.data.allTimeAverageSuccessRate;
  },

  async getBehaviorBreakdownForDay(
    familyId: number,
    date: string,
    childId?: number,
    familyTimezone?: string,
  ): Promise<{ stars: number; triangles: number; squares: number }> {
    const params = new URLSearchParams({ startDate: date, endDate: date, granularity: 'day' });
    if (childId !== undefined) params.append('childId', childId.toString());
    if (familyTimezone) params.append('familyTimezone', familyTimezone);
    const response = await api.get<{ period: string; stars: number; triangles: number; squares: number; flipYourCoins: number; makeItRights: number; oneOnOneTimes: number }[]>(
      `/api/family-insights/${familyId}/behavior-breakdown?${params.toString()}`
    );
    const day = response.data[0];
    if (!day) return { stars: 0, triangles: 0, squares: 0 };
    return { stars: day.stars, triangles: day.triangles, squares: day.squares };
  },

  async getChildCurrentHeartStreak(childId: number): Promise<number> {
    const response = await api.get<{ currentStreak: number }>(
      `/api/Behavior/stats/child/${childId}/streak/current`
    );
    return response.data.currentStreak;
  },
};

export const awayScheduleAPI = {
  async getSchedule(familyId: number): Promise<FamilyAwaySchedule> {
    const response = await api.get<FamilyAwaySchedule>(
      `/api/families/${familyId}/away-schedule`
    );
    return response.data;
  },

  async updateRecurringDays(familyId: number, daysOfWeek: number[]): Promise<FamilyAwaySchedule> {
    const response = await api.put<FamilyAwaySchedule>(
      `/api/families/${familyId}/away-schedule/recurring-days`,
      daysOfWeek
    );
    return response.data;
  },

  async setDateOverride(familyId: number, date: string, isAway: boolean | null): Promise<FamilyAwaySchedule> {
    const response = await api.put<FamilyAwaySchedule>(
      `/api/families/${familyId}/away-schedule/overrides/${date}`,
      { isAway }
    );
    return response.data;
  },

  async getAwayDatesInRange(familyId: number, start: string, end: string): Promise<string[]> {
    const response = await api.get<string[]>(
      `/api/families/${familyId}/away-schedule/away-dates?start=${start}&end=${end}`
    );
    return response.data;
  },
};

export default api;